// frontend/src/features/meetings/components/ScheduleMeetingModal.js
import React, { useState, useRef, useEffect, useMemo } from 'react';
import meetingsApi, { canJoinMeeting, getEffectiveMeetingStatus } from '../api/meetingsApi';
import calendarApi from '../api/calendarApi';

export const DEFAULT_WORKING_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
export const DAYS_OF_WEEK_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const formatTimeDisplay = (timeObjOrStr) => {
  if (!timeObjOrStr) return '';
  let dt;
  if (timeObjOrStr instanceof Date) {
    dt = timeObjOrStr;
  } else if (typeof timeObjOrStr === 'string' && timeObjOrStr.includes(':')) {
    const parts = timeObjOrStr.split('T');
    const timePart = parts.length > 1 ? parts[1] : parts[0];
    const [h, m] = timePart.split(':').map((v) => parseInt(v, 10) || 0);
    dt = new Date(2000, 0, 1, h, m, 0);
  } else {
    dt = new Date(timeObjOrStr);
  }
  if (isNaN(dt.getTime())) return String(timeObjOrStr);
  return dt.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const parseTimeToMinutes = (tStr = '', defaultMin = 540) => {
  if (!tStr) return defaultMin;
  const clean = String(tStr).trim();
  if (clean.includes('AM') || clean.includes('PM')) {
    const parts = clean.split(/\s+/);
    const [h, m] = (parts[0] || '09:00').split(':').map(Number);
    let hours = (h || 0) % 12;
    if (parts[1]?.toUpperCase() === 'PM') hours += 12;
    return hours * 60 + (m || 0);
  }
  const [h, m] = clean.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

export const generateHostTimeSlots = (startStr = '09:00', endStr = '18:00', durMins = 30) => {
  const startMin = parseTimeToMinutes(startStr, 9 * 60);
  const endMin = parseTimeToMinutes(endStr, 18 * 60);
  const dur = Number(durMins) || 30;
  const step = 30;
  const slots = [];
  for (let m = startMin; m + dur <= endMin; m += step) {
    const hh = String(Math.floor(m / 60)).padStart(2, '0');
    const mm = String(m % 60).padStart(2, '0');
    slots.push(`${hh}:${mm}`);
  }
  return slots.length > 0
    ? slots
    : [
        '09:00',
        '09:30',
        '10:00',
        '10:30',
        '11:00',
        '11:30',
        '13:30',
        '14:00',
        '14:30',
        '15:00',
        '15:30',
        '16:00',
        '16:30',
        '17:00',
      ];
};

const TIME_SLOTS = [
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
  '17:00',
];

const DURATION_OPTIONS = [15, 30, 45, 60, 90];

const DEFAULT_PIPELINE_STAGES = [
  'New',
  'Contacted',
  'Qualified',
  'Proposal',
  'Negotiation',
  'Won',
  'Lost',
];

const getAvatarColor = (name = '') => {
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#6366f1'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const ROLE_LABEL_MAP = {
  org_owner: 'Owner',
  org_admin: 'Admin',
  org_sales_manager: 'Sales Manager',
  org_sales_member: 'Sales Member',
  org_researcher: 'Researcher',
  org_viewer: 'Viewer',
  org_member: 'Member',
  sales_manager: 'Sales Manager',
  sales_member: 'Sales Member',
  sales_rep: 'Sales Rep',
  sales_lead: 'Sales Lead',
  account_executive: 'Account Executive',
  owner: 'Owner',
  admin: 'Admin',
  viewer: 'Viewer',
  member: 'Member',
  host: 'Host',
};

export const normalizeRoleTier = (role = '') => {
  if (!role) return 'member';
  const clean = String(role)
    .trim()
    .toLowerCase()
    .replace(/^org_/, '')
    .replace(/[\s-]+/g, '_');
  if (['owner'].includes(clean) || clean.includes('owner')) return 'owner';
  if (['admin', 'superadmin'].includes(clean) || clean.includes('admin')) return 'admin';
  if (
    [
      'manager',
      'sales_manager',
      'sales_lead',
      'lead',
      'director',
      'head',
      'sales_director',
      'sales_head',
    ].includes(clean) ||
    clean.includes('manager') ||
    clean.includes('lead') ||
    clean.includes('director') ||
    clean.includes('head')
  ) {
    return 'manager';
  }
  if (clean.includes('viewer')) return 'viewer';
  if (clean.includes('researcher')) return 'researcher';
  return 'member';
};

export const getRoleTierLevel = (role = '') => {
  const normalized = normalizeRoleTier(role);
  switch (normalized) {
    case 'owner':
      return 4;
    case 'admin':
      return 3;
    case 'manager':
      return 2;
    case 'member':
    case 'researcher':
      return 1;
    case 'viewer':
      return 0;
    default:
      return 1;
  }
};

const formatRoleLabel = (rawRole = '') => {
  if (!rawRole) return 'Host';
  const clean = String(rawRole).trim().toLowerCase();
  if (ROLE_LABEL_MAP[clean]) return ROLE_LABEL_MAP[clean];
  return (
    clean
      .replace(/^org_/, '')
      .split(/[_\-\s/]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ') || 'Host'
  );
};

export const getInitials = (name = '') => {
  if (!name) return 'TM';
  return (
    name
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('') || 'TM'
  );
};

export const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  MYR: 'RM',
  SGD: 'S$',
  AUD: 'A$',
  CAD: 'C$',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
};

export const formatCurrencyDisplay = (rawDeal, rawCurrency = 'USD') => {
  if (rawDeal === '' || rawDeal === null || rawDeal === undefined) return '';
  const curr = String(rawCurrency || 'USD').toUpperCase();
  const sym = CURRENCY_SYMBOLS[curr] || '$';
  const str = String(rawDeal).trim();

  // If already starts with currency symbol or code (e.g. "$25,000", "RM 50,000", "€10,000")
  if (
    str.startsWith('$') ||
    str.startsWith('RM') ||
    str.startsWith('€') ||
    str.startsWith('£') ||
    str.startsWith('S$') ||
    str.startsWith('¥') ||
    str.startsWith('₹') ||
    str.startsWith('A$') ||
    str.startsWith('C$')
  ) {
    // Clean up any redundant space or trailing uppercase code like " USD"
    return str
      .replace(/\s+[A-Z]{3}$/, '')
      .replace(/^(\$|€|£|¥|₹|S\$|A\$|C\$)\s+/, '$1')
      .trim();
  }

  const num = Number(str.replace(/[^0-9.-]+/g, ''));
  if (!isNaN(num) && str.replace(/[^0-9.-]+/g, '') !== '') {
    const needsSpace = sym === 'RM';
    return needsSpace ? `${sym} ${num.toLocaleString()}` : `${sym}${num.toLocaleString()}`;
  }
  return `${sym}${str}`;
};

export const extractEmailString = (rawEmail) => {
  if (!rawEmail) return '';
  if (typeof rawEmail === 'string') return rawEmail.trim();
  if (Array.isArray(rawEmail)) {
    for (const item of rawEmail) {
      const s = extractEmailString(item);
      if (s) return s;
    }
    return '';
  }
  if (typeof rawEmail === 'object') {
    if (rawEmail.email) return extractEmailString(rawEmail.email);
    if (rawEmail.value) return extractEmailString(rawEmail.value);
    if (rawEmail.address) return extractEmailString(rawEmail.address);
    if (rawEmail.contact_email) return extractEmailString(rawEmail.contact_email);
    return '';
  }
  return String(rawEmail).trim();
};

export const safeStringVal = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      const s = safeStringVal(item, '');
      if (s) return s;
    }
    return fallback;
  }
  if (typeof value === 'object') {
    for (const k of ['name', 'value', 'title', 'label', 'stage', 'company']) {
      if (value[k]) {
        const s = safeStringVal(value[k], '');
        if (s) return s;
      }
    }
    return fallback;
  }
  return String(value).trim();
};

const extractLeadDetails = (lead) => {
  if (!lead) return null;
  const crm = lead.crm_meta || {};

  const name = safeStringVal(lead.name || lead.full_name || crm.name, '');

  const rawEmail = lead.email || lead.contact_email || crm.email || crm.contact_email || '';
  const email = extractEmailString(rawEmail);

  const company = safeStringVal(
    lead.company || lead.company_name || crm.company || crm.companyName,
    ''
  );
  const jobTitle = safeStringVal(lead.job_title || lead.title || crm.jobTitle || crm.job_title, '');

  // Use the exact stage name directly from the CRM Lead Pipeline
  const stage = safeStringVal(lead.stage || crm.stage, 'New');

  const rawCurrency = safeStringVal(lead.currency || crm.currency, 'USD');
  const rawDeal = lead.deal_value ?? lead.dealValue ?? crm.dealValue ?? crm.deal_value ?? '';
  let dealEstimate = '';
  if (rawDeal !== '' && rawDeal !== null && rawDeal !== undefined) {
    dealEstimate = formatCurrencyDisplay(rawDeal, rawCurrency);
  } else {
    dealEstimate = '$0';
  }

  const score = lead.lead_score || lead.leadScore || crm.lead_score || 75;

  return {
    id: String(lead.id || lead.shared_item_id || lead.lead_id || name),
    name,
    email: String(email).trim(),
    company,
    jobTitle,
    stage,
    dealEstimate,
    currency: rawCurrency,
    leadScore: score,
    rawLead: lead,
  };
};

const isSlotInPast = (slotStr, selectedDateStr) => {
  if (!selectedDateStr || !slotStr) return false;
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const todayStr = `${y}-${m}-${d}`;

  if (selectedDateStr < todayStr) return true;
  if (selectedDateStr > todayStr) return false;

  // Selected date is today: compare hours and minutes
  const [slotH, slotM] = slotStr.split(':').map((v) => parseInt(v, 10) || 0);
  const slotTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), slotH, slotM, 0);
  return slotTime <= now;
};

export default function ScheduleMeetingModal({
  initialMeeting = null,
  hosts = [],
  meetingTypes = [],
  onClose,
  onSchedule,
  defaultHostId,
  defaultDate,
  workspaceId,
  pipelineLeads: propPipelineLeads,
  defaultSelectedLead = null,
  currentUser,
  currentUserRole,
  isOwner = false,
  existingMeetings = [],
  availabilitySettings = null,
}) {
  const isRescheduleMode = Boolean(initialMeeting);
  const isRescheduleLocked = useMemo(() => {
    if (!isRescheduleMode || !initialMeeting) return false;
    return (
      canJoinMeeting(initialMeeting) || getEffectiveMeetingStatus(initialMeeting) === 'in_progress'
    );
  }, [isRescheduleMode, initialMeeting]);

  const [externalBusySlots, setExternalBusySlots] = useState([]);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);

  const isCalendarConnected = useMemo(() => {
    if (!availabilitySettings || !Array.isArray(availabilitySettings.connectedCalendars)) {
      return true;
    }
    return availabilitySettings.connectedCalendars.some(
      (c) =>
        (c.provider === 'google' || c.provider === 'microsoft' || c.provider === 'outlook') &&
        c.status === 'connected'
    );
  }, [availabilitySettings]);

  const isGoogleCalendarConnected = isCalendarConnected;

  const effectiveCurrentUser = useMemo(() => {
    if (
      currentUser &&
      (currentUser.email || currentUser.id || currentUser.username || currentUser.name)
    ) {
      return currentUser;
    }
    try {
      const storedTokens =
        localStorage.getItem('keycloak_tokens') || sessionStorage.getItem('keycloak_tokens');
      if (storedTokens) {
        const parsed = JSON.parse(storedTokens);
        if (parsed?.access_token) {
          const base64Url = parsed.access_token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const tokenData = JSON.parse(atob(base64));
          if (tokenData) {
            return {
              id: tokenData.sub,
              email: tokenData.email,
              username: tokenData.preferred_username,
              name:
                tokenData.name ||
                [tokenData.given_name, tokenData.family_name].filter(Boolean).join(' ') ||
                tokenData.preferred_username,
            };
          }
        }
      }
    } catch (_) {}
    return currentUser || null;
  }, [currentUser]);

  const currentRoleLevel = useMemo(() => {
    if (isOwner) return 4;
    const currentMember = (hosts || []).find(
      (h) =>
        (effectiveCurrentUser?.id &&
          (String(h.id) === String(effectiveCurrentUser.id) ||
            String(h.userId) === String(effectiveCurrentUser.id))) ||
        (effectiveCurrentUser?.email &&
          h.email &&
          h.email.toLowerCase() === effectiveCurrentUser.email.toLowerCase()) ||
        (effectiveCurrentUser?.username &&
          h.username &&
          h.username.toLowerCase() === effectiveCurrentUser.username.toLowerCase()) ||
        (effectiveCurrentUser?.name &&
          h.name &&
          h.name.toLowerCase() === effectiveCurrentUser.name.toLowerCase())
    );
    const r =
      currentMember?.rawRole ||
      currentMember?.role ||
      currentUserRole ||
      effectiveCurrentUser?.workspace_role ||
      effectiveCurrentUser?.role;
    if (!r) return 4;
    return getRoleTierLevel(r);
  }, [isOwner, hosts, effectiveCurrentUser, currentUserRole]);

  const assignableHosts = useMemo(() => {
    if (!Array.isArray(hosts)) return [];
    return hosts.filter((h) => {
      // 1. Never show the current logged-in user themselves
      const isCurrentUser =
        (effectiveCurrentUser?.id &&
          (String(h.id) === String(effectiveCurrentUser.id) ||
            String(h.userId) === String(effectiveCurrentUser.id))) ||
        (effectiveCurrentUser?.email &&
          h.email &&
          h.email.toLowerCase() === effectiveCurrentUser.email.toLowerCase()) ||
        (effectiveCurrentUser?.username &&
          h.username &&
          h.username.toLowerCase() === effectiveCurrentUser.username.toLowerCase()) ||
        (effectiveCurrentUser?.name &&
          h.name &&
          h.name.toLowerCase() === effectiveCurrentUser.name.toLowerCase());

      if (isCurrentUser) return false;

      // 2. Only show hosts whose role level is strictly lower than current user's role level
      const hostRoleLevel = getRoleTierLevel(h?.rawRole || h?.role);
      return hostRoleLevel < currentRoleLevel;
    });
  }, [hosts, currentRoleLevel, effectiveCurrentUser]);

  const [hostId, setHostId] = useState(initialMeeting?.hostId || defaultHostId || '');
  const [meetingTypeId, setMeetingTypeId] = useState(
    initialMeeting?.meetingTypeId || initialMeeting?.meeting_type_id || meetingTypes[0]?.id || ''
  );

  const initialLeadDetails = useMemo(() => {
    if (defaultSelectedLead) {
      return extractLeadDetails(defaultSelectedLead);
    }
    return null;
  }, [defaultSelectedLead]);

  const [meetingTopic, setMeetingTopic] = useState(() => {
    if (initialMeeting?.title) {
      const colonIdx = initialMeeting.title.indexOf(':');
      if (colonIdx !== -1) {
        return initialMeeting.title.substring(colonIdx + 1).trim();
      }
      return initialMeeting.title;
    }
    if (defaultSelectedLead) {
      const details = extractLeadDetails(defaultSelectedLead);
      if (details?.name) return details.name;
    }
    return '';
  });

  const [hasUserEditedTopic, setHasUserEditedTopic] = useState(() =>
    Boolean(initialMeeting?.title)
  );

  const [guestName, setGuestName] = useState(
    initialMeeting?.guestName || initialLeadDetails?.name || ''
  );
  const [guestEmail, setGuestEmail] = useState(
    initialMeeting?.guestEmail || initialLeadDetails?.email || ''
  );
  const [guestCompany, setGuestCompany] = useState(
    initialMeeting?.guestCompany || initialLeadDetails?.company || ''
  );
  const [guestTitle, setGuestTitle] = useState(
    initialMeeting?.guestTitle || initialLeadDetails?.jobTitle || ''
  );
  const [guestNotes, setGuestNotes] = useState(initialMeeting?.guestNotes || '');
  const [dealEstimate, setDealEstimate] = useState(
    initialMeeting?.dealEstimate || initialLeadDetails?.dealEstimate || ''
  );
  const [currency, setCurrency] = useState(
    initialMeeting?.currency || initialLeadDetails?.currency || 'USD'
  );
  const [dealStage, setDealStage] = useState(
    initialMeeting?.dealStage || initialLeadDetails?.stage || 'New'
  );
  const [leadScore, setLeadScore] = useState(
    initialMeeting?.leadScore ?? initialLeadDetails?.leadScore ?? 75
  );
  const [durationMinutes, setDurationMinutes] = useState(initialMeeting?.durationMinutes ?? 30);

  const rescheduleAttendeesList = useMemo(() => {
    if (!initialMeeting) return [];

    // 1. Explicit attendees array
    if (Array.isArray(initialMeeting.attendees) && initialMeeting.attendees.length > 0) {
      return initialMeeting.attendees.map((att, idx) => ({
        id: att.id || att.leadId || `att-${idx}`,
        name: att.name || att.leadName || 'Attendee',
        email: att.email || att.guestEmail || '',
        company: att.company || att.guestCompany || '',
        jobTitle: att.jobTitle || att.title || att.guestTitle || '',
        stage: att.stage || att.dealStage || 'Contacted',
      }));
    }

    // 2. PreMeetingBrief attendees
    const briefAttendees =
      initialMeeting.preMeetingBrief?.attendees || initialMeeting.pre_meeting_brief?.attendees;
    if (Array.isArray(briefAttendees) && briefAttendees.length > 0) {
      return briefAttendees.map((att, idx) => ({
        id: att.id || att.leadId || `att-${idx}`,
        name: att.name || att.leadName || 'Attendee',
        email: att.email || att.guestEmail || '',
        company: att.company || att.guestCompany || '',
        jobTitle: att.jobTitle || att.title || att.guestTitle || '',
        stage: att.stage || att.dealStage || 'Contacted',
      }));
    }

    // 3. Comma-separated guest emails/names
    if (initialMeeting.guestEmail && initialMeeting.guestEmail.includes(',')) {
      const emailList = initialMeeting.guestEmail
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);
      const companyList = (initialMeeting.guestCompany || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
      const nameList = (initialMeeting.guestName || '').includes(',')
        ? initialMeeting.guestName
            .split(',')
            .map((n) => n.trim())
            .filter(Boolean)
        : [];

      return emailList.map((email, idx) => {
        let name = nameList[idx];
        if (
          !name &&
          idx === 0 &&
          initialMeeting.guestName &&
          initialMeeting.guestName.includes('(')
        ) {
          const match = initialMeeting.guestName.match(/\(([^)+]+)/);
          if (match && match[1]) name = match[1].trim();
        }
        if (!name) {
          const local = email.split('@')[0] || '';
          name =
            local
              .split(/[._-]/)
              .filter(Boolean)
              .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
              .join(' ') || `Attendee ${idx + 1}`;
        }
        return {
          id: `att-split-${idx}`,
          name: name,
          email: email,
          company: companyList[idx] || (companyList.length === 1 ? companyList[0] : ''),
          jobTitle:
            initialMeeting.guestTitle &&
            !initialMeeting.guestTitle.toLowerCase().includes('decision makers')
              ? initialMeeting.guestTitle
              : '',
          stage: 'Contacted',
        };
      });
    }

    if (initialMeeting.guestName || initialMeeting.guestEmail) {
      return [
        {
          id: initialMeeting.leadId || 'guest-1',
          name: initialMeeting.guestName || 'Invited Guest',
          email: initialMeeting.guestEmail || '',
          company: initialMeeting.guestCompany || '',
          jobTitle: initialMeeting.guestTitle || '',
          stage: initialMeeting.dealStage || 'New',
        },
      ];
    }

    return [];
  }, [initialMeeting]);

  const isRescheduleGroupSession =
    isRescheduleMode &&
    Boolean(
      initialMeeting?.isGroupMeeting ||
      initialMeeting?.is_group_meeting ||
      rescheduleAttendeesList.length > 1 ||
      initialMeeting?.campaignName ||
      initialMeeting?.campaignId
    );

  // Lead import mode: 'pipeline' vs 'manual'
  const [leadSourceMode, setLeadSourceMode] = useState(isRescheduleMode ? 'manual' : 'pipeline');
  const [pipelineLeads, setPipelineLeads] = useState(() => {
    if (Array.isArray(propPipelineLeads) && propPipelineLeads.length > 0) return propPipelineLeads;
    if (defaultSelectedLead) return [defaultSelectedLead];
    return [];
  });
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState(
    initialMeeting?.leadId || initialLeadDetails?.id || null
  );
  const [isLeadDropdownOpen, setIsLeadDropdownOpen] = useState(false);

  useEffect(() => {
    if (defaultSelectedLead) {
      const details = extractLeadDetails(defaultSelectedLead);
      if (details) {
        setSelectedLeadId(details.id);
        setGuestName(details.name);
        setGuestEmail(details.email);
        setGuestCompany(details.company);
        setGuestTitle(details.jobTitle);
        setDealStage(details.stage);
        setDealEstimate(details.dealEstimate);
        setCurrency(details.currency || 'USD');
        setLeadScore(details.leadScore);
        setLeadSourceMode('pipeline');
        if (details.name) {
          setMeetingTopic((prev) => (prev && prev.trim() ? prev : details.name));
        }
      }
    }
  }, [defaultSelectedLead]);

  const todayStr = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  const clearLeadFields = () => {
    setSelectedLeadId(null);
    setGuestName('');
    setGuestEmail('');
    setGuestCompany('');
    setGuestTitle('');
    setDealStage('New');
    setDealEstimate('');
    setCurrency('USD');
    setLeadScore(75);
    setLeadSearchQuery('');
    setIsLeadDropdownOpen(false);
    if (!hasUserEditedTopic) {
      setMeetingTopic('');
    }
  };

  const handleSwitchToPipeline = () => {
    if (leadSourceMode !== 'pipeline') {
      setLeadSourceMode('pipeline');
      clearLeadFields();
    }
  };

  const handleSwitchToManual = () => {
    if (leadSourceMode !== 'manual') {
      setLeadSourceMode('manual');
      clearLeadFields();
    }
  };

  const handleChangeLead = () => {
    clearLeadFields();
    setIsLeadDropdownOpen(true);
  };

  // Dynamic host working hours, working days, and timezone settings from availabilitySettings (Image 3)
  const hostWorkingDays = useMemo(() => {
    if (
      Array.isArray(availabilitySettings?.workingDays) &&
      availabilitySettings.workingDays.length > 0
    ) {
      return availabilitySettings.workingDays;
    }
    return DEFAULT_WORKING_DAYS;
  }, [availabilitySettings]);

  const hostWorkingHoursStart = availabilitySettings?.workingHoursStart || '09:00';
  const hostWorkingHoursEnd = availabilitySettings?.workingHoursEnd || '18:00';
  const hostTimezone = availabilitySettings?.timezone || 'Asia/Singapore (UTC+08:00)';
  const bufferBeforeMinutes = Number(
    availabilitySettings?.bufferBeforeMinutes ?? (availabilitySettings ? 15 : 0)
  );
  const bufferAfterMinutes = Number(
    availabilitySettings?.bufferAfterMinutes ?? (availabilitySettings ? 15 : 0)
  );

  const dynamicTimeSlots = useMemo(() => {
    const slots = generateHostTimeSlots(
      hostWorkingHoursStart,
      hostWorkingHoursEnd,
      durationMinutes
    );
    if (isRescheduleMode && initialMeeting?.startTime) {
      const dt = new Date(initialMeeting.startTime);
      if (!isNaN(dt.getTime())) {
        const hh = String(dt.getHours()).padStart(2, '0');
        const mm = String(dt.getMinutes()).padStart(2, '0');
        const currentSlot = `${hh}:${mm}`;
        if (!slots.includes(currentSlot)) {
          slots.push(currentSlot);
          slots.sort();
        }
      }
    }
    return slots;
  }, [
    hostWorkingHoursStart,
    hostWorkingHoursEnd,
    durationMinutes,
    isRescheduleMode,
    initialMeeting,
  ]);

  const isWorkingDay = (dateStr) => {
    if (!dateStr) return true;
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    const dayName = DAYS_OF_WEEK_FULL[dt.getDay()];
    return hostWorkingDays.some(
      (wd) =>
        wd.toLowerCase() === dayName.toLowerCase() ||
        wd.toLowerCase().startsWith(dayName.slice(0, 3).toLowerCase())
    );
  };

  const [date, setDate] = useState(() => {
    if (initialMeeting?.startTime) {
      const dt = new Date(initialMeeting.startTime);
      if (!isNaN(dt.getTime())) {
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        const d = String(dt.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
      return initialMeeting.startTime.split('T')[0];
    }
    if (defaultDate) {
      if (typeof defaultDate === 'string' && defaultDate.includes('T')) {
        return defaultDate.split('T')[0];
      }
      if (defaultDate instanceof Date && !isNaN(defaultDate.getTime())) {
        const y = defaultDate.getFullYear();
        const m = String(defaultDate.getMonth() + 1).padStart(2, '0');
        const d = String(defaultDate.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
      return String(defaultDate);
    }
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const todayFormatted = `${y}-${m}-${d}`;

    const initialSlots = generateHostTimeSlots(
      availabilitySettings?.workingHoursStart || '09:00',
      availabilitySettings?.workingHoursEnd || '18:00',
      initialMeeting?.durationMinutes ?? 30
    );

    const initialWorkingDays =
      Array.isArray(availabilitySettings?.workingDays) &&
      availabilitySettings.workingDays.length > 0
        ? availabilitySettings.workingDays
        : DEFAULT_WORKING_DAYS;

    const checkIsWorking = (dStr) => {
      const [yr, mo, dy] = dStr.split('-').map(Number);
      const dt = new Date(yr, mo - 1, dy);
      const dayName = DAYS_OF_WEEK_FULL[dt.getDay()];
      return initialWorkingDays.some(
        (wd) =>
          wd.toLowerCase() === dayName.toLowerCase() ||
          wd.toLowerCase().startsWith(dayName.slice(0, 3).toLowerCase())
      );
    };

    if (
      checkIsWorking(todayFormatted) &&
      initialSlots.some((s) => !isSlotInPast(s, todayFormatted))
    ) {
      return todayFormatted;
    }

    // Advance to next upcoming working day (up to 14 days ahead)
    for (let offset = 1; offset <= 14; offset++) {
      const nextDt = new Date(now);
      nextDt.setDate(now.getDate() + offset);
      const ny = nextDt.getFullYear();
      const nm = String(nextDt.getMonth() + 1).padStart(2, '0');
      const nd = String(nextDt.getDate()).padStart(2, '0');
      const nextDateStr = `${ny}-${nm}-${nd}`;
      if (checkIsWorking(nextDateStr)) {
        return nextDateStr;
      }
    }

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const ty = tomorrow.getFullYear();
    const tm = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const td = String(tomorrow.getDate()).padStart(2, '0');
    return `${ty}-${tm}-${td}`;
  });

  const [timeSlot, setTimeSlot] = useState(() => {
    if (initialMeeting?.startTime) {
      const dt = new Date(initialMeeting.startTime);
      if (!isNaN(dt.getTime())) {
        const hh = String(dt.getHours()).padStart(2, '0');
        const mm = String(dt.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
      }
      const timePart = initialMeeting.startTime.split('T')[1];
      if (timePart) {
        return timePart.substring(0, 5);
      }
    }
    const initialSlots = generateHostTimeSlots(
      availabilitySettings?.workingHoursStart || '09:00',
      availabilitySettings?.workingHoursEnd || '18:00',
      initialMeeting?.durationMinutes ?? 30
    );

    let targetDate = '';
    if (defaultDate) {
      if (typeof defaultDate === 'string' && defaultDate.includes('T')) {
        targetDate = defaultDate.split('T')[0];
        const timePart = defaultDate.split('T')[1];
        if (timePart) {
          const extracted = timePart.substring(0, 5);
          if (!isSlotInPast(extracted, targetDate)) {
            return extracted;
          }
        }
      } else if (typeof defaultDate === 'string') {
        targetDate = defaultDate;
      }
    }
    const initialDate =
      targetDate ||
      (() => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d = String(now.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      })();

    const firstFuture = initialSlots.find((s) => !isSlotInPast(s, initialDate));
    return firstFuture || initialSlots[0] || '09:00';
  });

  const connectedCalendarProvider = useMemo(() => {
    const cal = availabilitySettings?.connectedCalendars?.find(
      (c) =>
        (c.provider === 'google' || c.provider === 'microsoft' || c.provider === 'outlook') &&
        c.status === 'connected'
    );
    if (cal?.provider === 'microsoft' || cal?.provider === 'outlook') return 'microsoft';
    if (cal?.provider === 'google') return 'google';
    return null;
  }, [availabilitySettings]);

  const [conferencingProvider, setConferencingProvider] = useState(() => {
    if (initialMeeting?.conferencingProvider || initialMeeting?.conferencing) {
      return initialMeeting?.conferencingProvider || initialMeeting?.conferencing;
    }
    const cal = availabilitySettings?.connectedCalendars?.find(
      (c) =>
        (c.provider === 'google' || c.provider === 'microsoft' || c.provider === 'outlook') &&
        c.status === 'connected'
    );
    if (cal?.provider === 'microsoft' || cal?.provider === 'outlook') {
      return 'teams';
    }
    return 'google_meet';
  });

  const [platformNotice, setPlatformNotice] = useState(null);

  useEffect(() => {
    if (initialMeeting?.conferencingProvider || initialMeeting?.conferencing) return;
    if (connectedCalendarProvider === 'microsoft') {
      setConferencingProvider('teams');
    } else if (connectedCalendarProvider === 'google') {
      setConferencingProvider('google_meet');
    }
  }, [connectedCalendarProvider, initialMeeting]);

  // When date or dynamicTimeSlots changes, if current timeSlot is in the past or out of range, auto-advance to first future slot
  useEffect(() => {
    // In reschedule mode on the original date, keep the existing scheduled time slot
    if (isRescheduleMode && initialMeeting?.startTime) {
      const dt = new Date(initialMeeting.startTime);
      if (!isNaN(dt.getTime())) {
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        const d = String(dt.getDate()).padStart(2, '0');
        const origDate = `${y}-${m}-${d}`;
        const hh = String(dt.getHours()).padStart(2, '0');
        const mm = String(dt.getMinutes()).padStart(2, '0');
        const origSlot = `${hh}:${mm}`;
        if (date === origDate && timeSlot === origSlot) {
          return;
        }
      }
    }

    if (isSlotInPast(timeSlot, date) || !dynamicTimeSlots.includes(timeSlot)) {
      const firstFuture = dynamicTimeSlots.find((s) => !isSlotInPast(s, date));
      if (firstFuture) {
        setTimeSlot(firstFuture);
      } else if (dynamicTimeSlots.length > 0) {
        setTimeSlot(dynamicTimeSlots[0]);
      }
    }
  }, [date, timeSlot, dynamicTimeSlots, isRescheduleMode, initialMeeting]);

  // Dropdowns open states
  const [isHostOpen, setIsHostOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isTimeOpen, setIsTimeOpen] = useState(false);

  const hostRef = useRef(null);
  const typeRef = useRef(null);
  const timeRef = useRef(null);
  const leadDropdownRef = useRef(null);

  // Fetch pipeline leads on mount if not provided as prop
  useEffect(() => {
    let isMounted = true;
    if (propPipelineLeads && propPipelineLeads.length > 0) {
      setPipelineLeads(propPipelineLeads);
      return;
    }
    if (!workspaceId) return;

    setIsLoadingLeads(true);
    meetingsApi
      .getPipelineLeads(workspaceId)
      .then((leads) => {
        if (isMounted && Array.isArray(leads)) {
          setPipelineLeads(leads);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) setIsLoadingLeads(false);
      });

    return () => {
      isMounted = false;
    };
  }, [workspaceId, propPipelineLeads]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (hostRef.current && !hostRef.current.contains(e.target)) {
        setIsHostOpen(false);
      }
      if (typeRef.current && !typeRef.current.contains(e.target)) {
        setIsTypeOpen(false);
      }
      if (timeRef.current && !timeRef.current.contains(e.target)) {
        setIsTimeOpen(false);
      }
      if (leadDropdownRef.current && !leadDropdownRef.current.contains(e.target)) {
        setIsLeadDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const filteredPipelineLeads = useMemo(() => {
    if (!pipelineLeads || pipelineLeads.length === 0) return [];
    if (!leadSearchQuery.trim()) return pipelineLeads;
    const q = leadSearchQuery.toLowerCase().trim();
    return pipelineLeads.filter((lead) => {
      const d = extractLeadDetails(lead);
      if (!d) return false;
      return (
        d.name.toLowerCase().includes(q) ||
        d.company.toLowerCase().includes(q) ||
        d.email.toLowerCase().includes(q) ||
        d.jobTitle.toLowerCase().includes(q) ||
        d.stage.toLowerCase().includes(q)
      );
    });
  }, [pipelineLeads, leadSearchQuery]);

  const availableStages = useMemo(() => {
    const stageSet = new Set(DEFAULT_PIPELINE_STAGES);
    (pipelineLeads || []).forEach((lead) => {
      const s = lead.stage || lead.crm_meta?.stage;
      if (s && typeof s === 'string' && s.trim()) {
        stageSet.add(s.trim());
      }
    });
    if (dealStage && typeof dealStage === 'string' && dealStage.trim()) {
      stageSet.add(dealStage.trim());
    }
    return Array.from(stageSet);
  }, [pipelineLeads, dealStage]);

  const handleSelectPipelineLead = (lead) => {
    const conflict = getLeadConflictForSlot(lead);
    if (conflict) {
      return; // Cannot select lead who already has a meeting in this time slot
    }
    const details = extractLeadDetails(lead);
    if (!details) return;
    setSelectedLeadId(details.id);
    setGuestName(details.name);
    setGuestEmail(details.email);
    setGuestCompany(details.company);
    setGuestTitle(details.jobTitle);
    setDealStage(details.stage);
    setDealEstimate(details.dealEstimate);
    setCurrency(details.currency || 'USD');
    setLeadScore(details.leadScore);
    if (details.name) {
      setMeetingTopic((prev) =>
        !hasUserEditedTopic || !prev || !prev.trim() ? details.name : prev
      );
    }
    setIsLeadDropdownOpen(false);
  };

  const selectedHost = useMemo(() => {
    if (!hostId || hostId === 'unassigned') {
      return { id: '', name: 'Unassigned', email: '', role: '', isUnassigned: true };
    }
    const found =
      assignableHosts.find((h) => h.id === hostId || h.userId === hostId) ||
      hosts.find((h) => h.id === hostId || h.userId === hostId);
    if (found) return found;
    return { id: '', name: 'Unassigned', email: '', role: '', isUnassigned: true };
  }, [assignableHosts, hosts, hostId]);

  // Dynamic target person: if "Unassigned" or empty, automatically resolves to current account
  const effectiveTargetPerson = useMemo(() => {
    const isUnassigned = !hostId || hostId === 'unassigned';
    if (isUnassigned) {
      return {
        id:
          effectiveCurrentUser?.id ||
          effectiveCurrentUser?.userId ||
          effectiveCurrentUser?.sub ||
          '',
        userId:
          effectiveCurrentUser?.userId ||
          effectiveCurrentUser?.id ||
          effectiveCurrentUser?.sub ||
          '',
        name:
          effectiveCurrentUser?.name ||
          effectiveCurrentUser?.fullName ||
          effectiveCurrentUser?.displayName ||
          effectiveCurrentUser?.username ||
          'Current Account',
        email: effectiveCurrentUser?.email || '',
        isUnassigned: true,
        isSelf: true,
      };
    }
    const found =
      assignableHosts.find((h) => h.id === hostId || h.userId === hostId) ||
      hosts.find((h) => h.id === hostId || h.userId === hostId);
    if (found) {
      return {
        ...found,
        id: found.id || found.userId || hostId,
        userId: found.userId || found.id || hostId,
        name: found.name || found.username || 'Host',
        email: found.email || '',
        isUnassigned: false,
        isSelf: false,
      };
    }
    return {
      id: hostId,
      userId: hostId,
      name: 'Host',
      email: '',
      isUnassigned: false,
      isSelf: false,
    };
  }, [hostId, assignableHosts, hosts, effectiveCurrentUser]);

  const selectedType = useMemo(() => {
    return (
      meetingTypes.find((t) => t.id === meetingTypeId) ||
      meetingTypes[0] || {
        id: 'mt-default',
        title: 'Lead Discovery',
        color: '#3b82f6',
      }
    );
  }, [meetingTypes, meetingTypeId]);

  const formattedDisplayDate = useMemo(() => {
    if (!date) return '';
    try {
      const parts = date.split('-');
      if (parts.length === 3) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10) - 1;
        const d = parseInt(parts[2], 10);
        const dt = new Date(y, m, d);
        if (!isNaN(dt.getTime())) {
          return dt.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
        }
      }
      const dt = new Date(date);
      if (!isNaN(dt.getTime())) {
        return dt.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        });
      }
      return date;
    } catch {
      return date;
    }
  }, [date]);

  // Query live Google Calendar / Outlook external busy slots dynamically for the selected person (or current account if unassigned)
  useEffect(() => {
    let isMounted = true;
    if (!workspaceId || !date || workspaceId === 'ws-primary') {
      setExternalBusySlots([]);
      return;
    }

    const parts = date.split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return;
    const [y, m, d] = parts;

    const startOfDay = new Date(Date.UTC(y, m - 1, d, 0, 0, 0));
    const endOfDay = new Date(Date.UTC(y, m - 1, d, 23, 59, 59));

    const targetPersonId = effectiveTargetPerson.isSelf
      ? effectiveCurrentUser?.id || effectiveCurrentUser?.userId || effectiveCurrentUser?.sub || ''
      : effectiveTargetPerson.userId || effectiveTargetPerson.id || hostId;

    setIsCheckingConflicts(true);
    if (typeof calendarApi?.getExternalBusySlots === 'function') {
      calendarApi
        .getExternalBusySlots(
          workspaceId,
          startOfDay.toISOString(),
          endOfDay.toISOString(),
          targetPersonId
        )
        .then((slots) => {
          if (isMounted) {
            setExternalBusySlots(Array.isArray(slots) ? slots : []);
          }
        })
        .catch(() => {
          if (isMounted) setExternalBusySlots([]);
        })
        .finally(() => {
          if (isMounted) setIsCheckingConflicts(false);
        });
    } else {
      setIsCheckingConflicts(false);
    }

    return () => {
      isMounted = false;
    };
  }, [workspaceId, date, hostId, effectiveTargetPerson, effectiveCurrentUser]);

  const formatTimeDisplay = (timeObjOrStr) => {
    if (!timeObjOrStr) return '';
    let dt;
    if (timeObjOrStr instanceof Date) {
      dt = timeObjOrStr;
    } else if (typeof timeObjOrStr === 'string' && timeObjOrStr.includes(':')) {
      const parts = timeObjOrStr.split('T');
      const timePart = parts.length > 1 ? parts[1] : parts[0];
      const [h, m] = timePart.split(':').map((v) => parseInt(v, 10) || 0);
      dt = new Date(2000, 0, 1, h, m, 0);
    } else {
      dt = new Date(timeObjOrStr);
    }
    if (isNaN(dt.getTime())) return String(timeObjOrStr);
    return dt.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatSlotTimeStr = (dateObj) => {
    return formatTimeDisplay(dateObj);
  };

  const computedStartTime = useMemo(() => {
    return formatTimeDisplay(timeSlot);
  }, [timeSlot]);

  const computedEndTime = useMemo(() => {
    if (!timeSlot) return '';
    const [h, m] = timeSlot.split(':').map((v) => parseInt(v, 10) || 0);
    const dt = new Date(2000, 0, 1, h, m + (Number(durationMinutes) || 30));
    return formatTimeDisplay(dt);
  }, [timeSlot, durationMinutes]);

  const computedTimeRange = useMemo(() => {
    if (!computedStartTime || !computedEndTime) return '';
    return `${computedStartTime} - ${computedEndTime}`;
  }, [computedStartTime, computedEndTime]);

  const getSlotConflictInfo = useMemo(() => {
    return (slotStr, durMinutes = durationMinutes, targetDateStr = date) => {
      if (!targetDateStr || !slotStr) return null;
      const [slotH, slotM] = slotStr.split(':').map((v) => parseInt(v, 10) || 0);
      const [y, m, d] = targetDateStr.split('-').map((v) => parseInt(v, 10) || 0);
      const slotStart = new Date(y, m - 1, d, slotH, slotM, 0);
      const slotEnd = new Date(slotStart.getTime() + (Number(durMinutes) || 30) * 60000);

      // 1. Check against external calendar busy slots (Google Calendar / Microsoft Outlook)
      if (Array.isArray(externalBusySlots) && externalBusySlots.length > 0) {
        for (const busy of externalBusySlots) {
          if (!busy.start) continue;
          let bStart = new Date(busy.start);
          let bEnd = busy.end ? new Date(busy.end) : new Date(bStart.getTime() + 30 * 60000);
          let isOverlapping = slotStart < bEnd && slotEnd > bStart;

          if (
            !isOverlapping &&
            typeof busy.start === 'string' &&
            busy.start.includes(targetDateStr)
          ) {
            const timePart = busy.start.split('T')[1];
            if (timePart) {
              const [bH, bM] = timePart.substring(0, 5).split(':').map(Number);
              const bStartLocal = new Date(y, m - 1, d, bH, bM, 0);
              const bEndLocal = new Date(bStartLocal.getTime() + 30 * 60000);
              if (slotStart < bEndLocal && slotEnd > bStartLocal) {
                isOverlapping = true;
                bStart = bStartLocal;
                bEnd = bEndLocal;
              }
            }
          }

          if (isOverlapping) {
            const providerName =
              busy.provider === 'outlook' ? 'Microsoft Outlook' : 'Google Calendar';
            return {
              type: 'external',
              source: busy.provider || 'google',
              sourceLabel: providerName,
              title: busy.title || `${providerName} Personal Event`,
              timeRange: `${formatSlotTimeStr(bStart)} - ${formatSlotTimeStr(bEnd)}`,
              accountEmail: busy.accountEmail || '',
              hostName: busy.hostName || effectiveTargetPerson.name || '',
            };
          }
        }
      }

      // 2. Check against existing CRM meetings in the workspace on that date
      if (Array.isArray(existingMeetings) && existingMeetings.length > 0) {
        for (const mtg of existingMeetings) {
          if (!mtg.startTime) continue;
          // Skip if current meeting being rescheduled
          if (
            isRescheduleMode &&
            initialMeeting?.id &&
            String(mtg.id) === String(initialMeeting.id)
          ) {
            continue;
          }
          // Skip cancelled meetings
          if (mtg.status === 'cancelled') continue;

          // Dynamic conflict detection based on selected person (or current account if unassigned)
          const mtgHostId = String(mtg.hostId || mtg.host_id || '')
            .trim()
            .toLowerCase();
          const mtgHostEmail = String(mtg.hostEmail || mtg.host_email || '')
            .trim()
            .toLowerCase();
          const mtgHostName = String(mtg.hostName || mtg.host_name || '')
            .trim()
            .toLowerCase();
          const mtgAssignedStaff = String(
            mtg.preMeetingBrief?.assignedStaffName || mtg.pre_meeting_brief?.assignedStaffName || ''
          )
            .trim()
            .toLowerCase();

          let isAssignedToTarget = false;

          if (effectiveTargetPerson.isSelf) {
            // Target is Current Account / Unassigned
            const myIds = [
              effectiveCurrentUser?.id,
              effectiveCurrentUser?.userId,
              effectiveCurrentUser?.sub,
              effectiveCurrentUser?.user_id,
            ]
              .filter(Boolean)
              .map((id) => String(id).trim().toLowerCase());

            const myEmails = [effectiveCurrentUser?.email]
              .filter(Boolean)
              .map((e) => String(e).trim().toLowerCase());

            const myNames = [
              effectiveCurrentUser?.name,
              effectiveCurrentUser?.fullName,
              effectiveCurrentUser?.displayName,
              effectiveCurrentUser?.username,
            ]
              .filter(Boolean)
              .map((n) => String(n).trim().toLowerCase());

            const isUnassignedMtg =
              !mtgHostId || mtgHostId === 'unassigned' || mtgHostId === 'usr-1';
            const isMyMtg =
              myIds.includes(mtgHostId) ||
              myEmails.some((em) => em && (em === mtgHostEmail || mtgHostEmail.includes(em))) ||
              myNames.some((nm) => nm && (nm === mtgHostName || mtgHostName.includes(nm)));

            isAssignedToTarget = isUnassignedMtg || isMyMtg;
          } else {
            // Target is a specific selected team member (e.g. Alice Tan)
            const targetIds = [effectiveTargetPerson.id, effectiveTargetPerson.userId, hostId]
              .filter(Boolean)
              .map((id) => String(id).trim().toLowerCase());

            const targetEmail = String(effectiveTargetPerson.email || '')
              .trim()
              .toLowerCase();
            const targetName = String(
              effectiveTargetPerson.name || effectiveTargetPerson.username || ''
            )
              .trim()
              .toLowerCase();

            isAssignedToTarget =
              targetIds.includes(mtgHostId) ||
              (targetEmail &&
                (mtgHostEmail === targetEmail || mtgHostEmail.includes(targetEmail))) ||
              (targetName &&
                (mtgHostName === targetName ||
                  mtgHostName.includes(targetName) ||
                  mtgAssignedStaff === targetName ||
                  mtgAssignedStaff.includes(targetName)));
          }

          if (!isAssignedToTarget) {
            continue; // Not assigned to this person, so skip (no conflict for this person)
          }

          let mStart = new Date(mtg.startTime);
          let mEnd = mtg.endTime
            ? new Date(mtg.endTime)
            : new Date(mStart.getTime() + (Number(mtg.durationMinutes) || 30) * 60000);

          let isOverlapping = slotStart < mEnd && slotEnd > mStart;

          if (
            !isOverlapping &&
            typeof mtg.startTime === 'string' &&
            mtg.startTime.includes(targetDateStr)
          ) {
            const timePart = mtg.startTime.split('T')[1];
            if (timePart) {
              const [mH, mM] = timePart.substring(0, 5).split(':').map(Number);
              const mStartLocal = new Date(y, m - 1, d, mH, mM, 0);
              const mEndLocal = new Date(
                mStartLocal.getTime() + (Number(mtg.durationMinutes) || 30) * 60000
              );
              if (slotStart < mEndLocal && slotEnd > mStartLocal) {
                isOverlapping = true;
                mStart = mStartLocal;
                mEnd = mEndLocal;
              }
            }
          }

          if (isOverlapping) {
            return {
              type: 'crm_meeting',
              source: 'crm',
              sourceLabel: 'CRM Meeting',
              title: mtg.title || `Meeting: ${mtg.guestName || 'Lead'}`,
              timeRange: `${formatSlotTimeStr(mStart)} - ${formatSlotTimeStr(mEnd)}`,
              guestName: mtg.guestName || '',
              hostName: mtg.hostName || effectiveTargetPerson.name || '',
            };
          }
        }
      }

      // 3. Check buffer conflicts if bufferBeforeMinutes or bufferAfterMinutes are configured
      const effectiveBufBefore = Number(
        effectiveTargetPerson?.bufferBeforeMinutes ??
          effectiveTargetPerson?.buffer_before_minutes ??
          bufferBeforeMinutes
      );
      const effectiveBufAfter = Number(
        effectiveTargetPerson?.bufferAfterMinutes ??
          effectiveTargetPerson?.buffer_after_minutes ??
          bufferAfterMinutes
      );
      const bufBeforeMs = (effectiveBufBefore || 0) * 60000;
      const bufAfterMs = (effectiveBufAfter || 0) * 60000;

      if (bufBeforeMs > 0 || bufAfterMs > 0) {
        // 3a. Buffer check against external calendar busy slots
        if (Array.isArray(externalBusySlots) && externalBusySlots.length > 0) {
          for (const busy of externalBusySlots) {
            if (!busy.start) continue;
            let bStart = new Date(busy.start);
            let bEnd = busy.end ? new Date(busy.end) : new Date(bStart.getTime() + 30 * 60000);

            if (typeof busy.start === 'string' && busy.start.includes(targetDateStr)) {
              const timePart = busy.start.split('T')[1];
              if (timePart) {
                const [bH, bM] = timePart.substring(0, 5).split(':').map(Number);
                bStart = new Date(y, m - 1, d, bH, bM, 0);
                bEnd = new Date(bStart.getTime() + 30 * 60000);
              }
            }

            if (Math.abs(slotStart.getTime() - bStart.getTime()) > 24 * 3600000) continue;

            const providerName =
              busy.provider === 'outlook' ? 'Microsoft Outlook' : 'Google Calendar';

            // Slot is AFTER external event: check buffer after event
            if (slotStart.getTime() >= bEnd.getTime()) {
              const gapMs = slotStart.getTime() - bEnd.getTime();
              const requiredGapMs = Math.max(bufAfterMs, bufBeforeMs);
              if (gapMs >= 0 && gapMs < requiredGapMs) {
                const neededMins = Math.round(requiredGapMs / 60000);
                const availTimeStr = formatSlotTimeStr(new Date(bEnd.getTime() + requiredGapMs));
                return {
                  type: 'external',
                  isBuffer: true,
                  bufferMinutes: neededMins,
                  bufferDirection: 'after',
                  source: busy.provider || 'google',
                  sourceLabel: providerName,
                  title: busy.title || `${providerName} Personal Event`,
                  timeRange: `${formatSlotTimeStr(bStart)} - ${formatSlotTimeStr(bEnd)}`,
                  bufferNotice: `Requires ${neededMins}m buffer after event (ends at ${formatSlotTimeStr(bEnd)}). Available from ${availTimeStr}.`,
                  accountEmail: busy.accountEmail || '',
                  hostName: busy.hostName || effectiveTargetPerson.name || '',
                };
              }
            }

            // Slot is BEFORE external event: check buffer before event
            if (slotEnd.getTime() <= bStart.getTime()) {
              const gapMs = bStart.getTime() - slotEnd.getTime();
              const requiredGapMs = Math.max(bufBeforeMs, bufAfterMs);
              if (gapMs >= 0 && gapMs < requiredGapMs) {
                const neededMins = Math.round(requiredGapMs / 60000);
                const availUntilStr = formatSlotTimeStr(new Date(bStart.getTime() - requiredGapMs));
                return {
                  type: 'external',
                  isBuffer: true,
                  bufferMinutes: neededMins,
                  bufferDirection: 'before',
                  source: busy.provider || 'google',
                  sourceLabel: providerName,
                  title: busy.title || `${providerName} Personal Event`,
                  timeRange: `${formatSlotTimeStr(bStart)} - ${formatSlotTimeStr(bEnd)}`,
                  bufferNotice: `Requires ${neededMins}m buffer before event (starts at ${formatSlotTimeStr(bStart)}). Available until ${availUntilStr}.`,
                  accountEmail: busy.accountEmail || '',
                  hostName: busy.hostName || effectiveTargetPerson.name || '',
                };
              }
            }
          }
        }

        // 3b. Buffer check against CRM meetings for target host
        if (Array.isArray(existingMeetings) && existingMeetings.length > 0) {
          for (const mtg of existingMeetings) {
            if (!mtg.startTime) continue;
            if (
              isRescheduleMode &&
              initialMeeting?.id &&
              String(mtg.id) === String(initialMeeting.id)
            ) {
              continue;
            }
            if (mtg.status === 'cancelled') continue;

            const mtgHostId = String(mtg.hostId || mtg.host_id || '')
              .trim()
              .toLowerCase();
            const mtgHostEmail = String(mtg.hostEmail || mtg.host_email || '')
              .trim()
              .toLowerCase();
            const mtgHostName = String(mtg.hostName || mtg.host_name || '')
              .trim()
              .toLowerCase();
            const mtgAssignedStaff = String(
              mtg.preMeetingBrief?.assignedStaffName ||
                mtg.pre_meeting_brief?.assignedStaffName ||
                ''
            )
              .trim()
              .toLowerCase();

            let isAssignedToTarget = false;

            if (effectiveTargetPerson.isSelf) {
              const myIds = [
                effectiveCurrentUser?.id,
                effectiveCurrentUser?.userId,
                effectiveCurrentUser?.sub,
                effectiveCurrentUser?.user_id,
              ]
                .filter(Boolean)
                .map((id) => String(id).trim().toLowerCase());

              const myEmails = [effectiveCurrentUser?.email]
                .filter(Boolean)
                .map((e) => String(e).trim().toLowerCase());

              const myNames = [
                effectiveCurrentUser?.name,
                effectiveCurrentUser?.fullName,
                effectiveCurrentUser?.displayName,
                effectiveCurrentUser?.username,
              ]
                .filter(Boolean)
                .map((n) => String(n).trim().toLowerCase());

              const isUnassignedMtg =
                !mtgHostId || mtgHostId === 'unassigned' || mtgHostId === 'usr-1';
              const isMyMtg =
                myIds.includes(mtgHostId) ||
                myEmails.some((em) => em && (em === mtgHostEmail || mtgHostEmail.includes(em))) ||
                myNames.some((nm) => nm && (nm === mtgHostName || mtgHostName.includes(nm)));

              isAssignedToTarget = isUnassignedMtg || isMyMtg;
            } else {
              const targetIds = [effectiveTargetPerson.id, effectiveTargetPerson.userId, hostId]
                .filter(Boolean)
                .map((id) => String(id).trim().toLowerCase());

              const targetEmail = String(effectiveTargetPerson.email || '')
                .trim()
                .toLowerCase();
              const targetName = String(
                effectiveTargetPerson.name || effectiveTargetPerson.username || ''
              )
                .trim()
                .toLowerCase();

              isAssignedToTarget =
                targetIds.includes(mtgHostId) ||
                (targetEmail &&
                  (mtgHostEmail === targetEmail || mtgHostEmail.includes(targetEmail))) ||
                (targetName &&
                  (mtgHostName === targetName ||
                    mtgHostName.includes(targetName) ||
                    mtgAssignedStaff === targetName ||
                    mtgAssignedStaff.includes(targetName)));
            }

            if (!isAssignedToTarget) continue;

            let mStart = new Date(mtg.startTime);
            let mEnd = mtg.endTime
              ? new Date(mtg.endTime)
              : new Date(mStart.getTime() + (Number(mtg.durationMinutes) || 30) * 60000);

            if (typeof mtg.startTime === 'string' && mtg.startTime.includes(targetDateStr)) {
              const timePart = mtg.startTime.split('T')[1];
              if (timePart) {
                const [mH, mM] = timePart.substring(0, 5).split(':').map(Number);
                mStart = new Date(y, m - 1, d, mH, mM, 0);
                mEnd = new Date(mStart.getTime() + (Number(mtg.durationMinutes) || 30) * 60000);
              }
            }

            if (Math.abs(slotStart.getTime() - mStart.getTime()) > 24 * 3600000) continue;

            // Slot is AFTER CRM meeting: check buffer after meeting
            if (slotStart.getTime() >= mEnd.getTime()) {
              const gapMs = slotStart.getTime() - mEnd.getTime();
              const requiredGapMs = Math.max(bufAfterMs, bufBeforeMs);
              if (gapMs >= 0 && gapMs < requiredGapMs) {
                const neededMins = Math.round(requiredGapMs / 60000);
                const availTimeStr = formatSlotTimeStr(new Date(mEnd.getTime() + requiredGapMs));
                return {
                  type: 'crm_meeting',
                  isBuffer: true,
                  bufferMinutes: neededMins,
                  bufferDirection: 'after',
                  source: 'crm',
                  sourceLabel: 'CRM Meeting',
                  title: mtg.title || `Meeting: ${mtg.guestName || 'Lead'}`,
                  timeRange: `${formatSlotTimeStr(mStart)} - ${formatSlotTimeStr(mEnd)}`,
                  bufferNotice: `Requires ${neededMins}m buffer after meeting (ends at ${formatSlotTimeStr(mEnd)}). Available from ${availTimeStr}.`,
                  guestName: mtg.guestName || '',
                  hostName: mtg.hostName || effectiveTargetPerson.name || '',
                };
              }
            }

            // Slot is BEFORE CRM meeting: check buffer before meeting
            if (slotEnd.getTime() <= mStart.getTime()) {
              const gapMs = mStart.getTime() - slotEnd.getTime();
              const requiredGapMs = Math.max(bufBeforeMs, bufAfterMs);
              if (gapMs >= 0 && gapMs < requiredGapMs) {
                const neededMins = Math.round(requiredGapMs / 60000);
                const availUntilStr = formatSlotTimeStr(new Date(mStart.getTime() - requiredGapMs));
                return {
                  type: 'crm_meeting',
                  isBuffer: true,
                  bufferMinutes: neededMins,
                  bufferDirection: 'before',
                  source: 'crm',
                  sourceLabel: 'CRM Meeting',
                  title: mtg.title || `Meeting: ${mtg.guestName || 'Lead'}`,
                  timeRange: `${formatSlotTimeStr(mStart)} - ${formatSlotTimeStr(mEnd)}`,
                  bufferNotice: `Requires ${neededMins}m buffer before next meeting (starts at ${formatSlotTimeStr(mStart)}). Available until ${availUntilStr}.`,
                  guestName: mtg.guestName || '',
                  hostName: mtg.hostName || effectiveTargetPerson.name || '',
                };
              }
            }
          }
        }
      }

      return null;
    };
  }, [
    date,
    durationMinutes,
    externalBusySlots,
    existingMeetings,
    isRescheduleMode,
    initialMeeting,
    effectiveTargetPerson,
    effectiveCurrentUser,
    hostId,
    bufferBeforeMinutes,
    bufferAfterMinutes,
  ]);

  // Precompute conflict mapping for all dynamic host time slots
  const slotsConflictMap = useMemo(() => {
    const map = {};
    dynamicTimeSlots.forEach((slot) => {
      map[slot] = getSlotConflictInfo(slot, durationMinutes);
    });
    return map;
  }, [getSlotConflictInfo, durationMinutes, dynamicTimeSlots]);

  const currentSlotConflict = useMemo(() => {
    return slotsConflictMap[timeSlot] || null;
  }, [slotsConflictMap, timeSlot]);

  // Smart multi-day slot suggestions respecting host working days & hours (Image 3)
  const smartMultiDaySuggestions = useMemo(() => {
    if (!currentSlotConflict || !date) return [];

    const suggestions = [];
    const durMins = Number(durationMinutes) || 30;

    const getDateLabel = (dStr) => {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      const todayString = `${y}-${m}-${d}`;

      if (dStr === todayString) return 'Today';

      const [year, month, day] = dStr.split('-').map(Number);
      const dt = new Date(year, month - 1, day);
      return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    // 1. Same-day available slots (if host working day)
    if (isWorkingDay(date)) {
      const sameDaySlots = dynamicTimeSlots.filter(
        (s) => !isSlotInPast(s, date) && !slotsConflictMap[s]
      );
      sameDaySlots.slice(0, 3).forEach((s) => {
        suggestions.push({
          dateStr: date,
          dateLabel: getDateLabel(date),
          slot: s,
          timeLabel: formatTimeDisplay(s),
          isCurrentSelectedDate: true,
        });
      });
    }

    // 2. Scan upcoming days (up to 14 days ahead, discovering host working days)
    const [y, m, d] = date.split('-').map(Number);
    const baseDate = new Date(y, m - 1, d);

    let workingDaysCount = 0;
    for (let offset = 1; offset <= 14; offset++) {
      if (suggestions.length >= 8 || workingDaysCount >= 3) break;
      const nextDt = new Date(baseDate);
      nextDt.setDate(baseDate.getDate() + offset);

      const ny = nextDt.getFullYear();
      const nm = String(nextDt.getMonth() + 1).padStart(2, '0');
      const nd = String(nextDt.getDate()).padStart(2, '0');
      const nextDateStr = `${ny}-${nm}-${nd}`;

      // Strictly respect host working days from availabilitySettings (Image 3)
      if (!isWorkingDay(nextDateStr)) continue;

      let slotsAddedForDay = 0;
      for (const s of dynamicTimeSlots) {
        if (slotsAddedForDay >= 3) break;
        if (suggestions.length >= 8) break;

        const conflict = getSlotConflictInfo(s, durMins, nextDateStr);
        if (!conflict) {
          suggestions.push({
            dateStr: nextDateStr,
            dateLabel: getDateLabel(nextDateStr),
            slot: s,
            timeLabel: formatTimeDisplay(s),
            isCurrentSelectedDate: false,
          });
          slotsAddedForDay++;
        }
      }

      if (slotsAddedForDay > 0) {
        workingDaysCount++;
      }
    }

    return suggestions;
  }, [
    currentSlotConflict,
    date,
    durationMinutes,
    isWorkingDay,
    dynamicTimeSlots,
    slotsConflictMap,
    getSlotConflictInfo,
  ]);

  // Dynamic lead conflict detection: detect if a lead already has an existing meeting scheduled in the target time slot
  const getLeadConflictForSlot = useMemo(() => {
    return (
      leadItem,
      targetDateStr = date,
      targetSlotStr = timeSlot,
      durMins = durationMinutes
    ) => {
      if (!leadItem || !targetDateStr || !targetSlotStr || !Array.isArray(existingMeetings))
        return null;

      const leadDetails = extractLeadDetails(leadItem);
      if (!leadDetails) return null;

      const [slotH, slotM] = targetSlotStr.split(':').map((v) => parseInt(v, 10) || 0);
      const [y, m, d] = targetDateStr.split('-').map((v) => parseInt(v, 10) || 0);
      const slotStart = new Date(y, m - 1, d, slotH, slotM, 0);
      const slotEnd = new Date(slotStart.getTime() + (Number(durMins) || 30) * 60000);

      const leadId = String(leadDetails.id || '')
        .trim()
        .toLowerCase();
      const leadEmail = String(leadDetails.email || '')
        .trim()
        .toLowerCase();
      const leadName = String(leadDetails.name || '')
        .trim()
        .toLowerCase();

      for (const mtg of existingMeetings) {
        if (!mtg.startTime || mtg.status === 'cancelled') continue;
        if (
          isRescheduleMode &&
          initialMeeting?.id &&
          String(mtg.id) === String(initialMeeting.id)
        ) {
          continue;
        }

        const mtgLeadId = String(mtg.leadId || mtg.lead_id || '')
          .trim()
          .toLowerCase();
        const mtgGuestEmail = String(mtg.guestEmail || mtg.guest_email || '')
          .trim()
          .toLowerCase();
        const mtgGuestName = String(mtg.guestName || mtg.guest_name || '')
          .trim()
          .toLowerCase();

        // 1. Direct ID match
        let isSameLead = Boolean(
          leadId &&
          mtgLeadId &&
          (leadId === mtgLeadId || leadId.includes(mtgLeadId) || mtgLeadId.includes(leadId))
        );

        // 2. Check leadIds array (top-level or inside preMeetingBrief)
        if (!isSameLead) {
          const leadIdsList = [
            ...(Array.isArray(mtg.leadIds) ? mtg.leadIds : []),
            ...(Array.isArray(mtg.lead_ids) ? mtg.lead_ids : []),
            ...(Array.isArray(mtg.preMeetingBrief?.leadIds) ? mtg.preMeetingBrief.leadIds : []),
            ...(Array.isArray(mtg.pre_meeting_brief?.leadIds) ? mtg.pre_meeting_brief.leadIds : []),
          ].map((id) => String(id).trim().toLowerCase());

          if (
            leadId &&
            leadIdsList.some(
              (id) => id && (id === leadId || id.includes(leadId) || leadId.includes(id))
            )
          ) {
            isSameLead = true;
          }
        }

        // 3. Check attendees array (top-level or inside preMeetingBrief)
        if (!isSameLead) {
          const attendeesList = [
            ...(Array.isArray(mtg.attendees) ? mtg.attendees : []),
            ...(Array.isArray(mtg.preMeetingBrief?.attendees) ? mtg.preMeetingBrief.attendees : []),
            ...(Array.isArray(mtg.pre_meeting_brief?.attendees)
              ? mtg.pre_meeting_brief.attendees
              : []),
          ];

          for (const att of attendeesList) {
            if (!att) continue;
            if (typeof att === 'string') {
              const attStr = att.trim().toLowerCase();
              if (leadEmail && attStr.includes(leadEmail)) {
                isSameLead = true;
                break;
              }
              if (leadName && attStr.includes(leadName)) {
                isSameLead = true;
                break;
              }
              if (leadId && attStr === leadId) {
                isSameLead = true;
                break;
              }
            } else if (typeof att === 'object') {
              const attId = String(att.id || att.crmId || att.leadId || att.lead_id || '')
                .trim()
                .toLowerCase();
              const attEmail = String(att.email || att.contact_email || '')
                .trim()
                .toLowerCase();
              const attName = String(att.name || att.full_name || '')
                .trim()
                .toLowerCase();

              if (
                leadId &&
                attId &&
                (leadId === attId || leadId.includes(attId) || attId.includes(leadId))
              ) {
                isSameLead = true;
                break;
              }
              if (
                leadEmail &&
                attEmail &&
                (leadEmail === attEmail ||
                  attEmail.includes(leadEmail) ||
                  leadEmail.includes(attEmail))
              ) {
                isSameLead = true;
                break;
              }
              if (
                leadName &&
                attName &&
                (leadName === attName || attName.includes(leadName) || leadName.includes(attName))
              ) {
                isSameLead = true;
                break;
              }
            }
          }
        }

        // 4. Check guestEmail (exact or comma-separated/substring match)
        if (!isSameLead && leadEmail && mtgGuestEmail) {
          if (leadEmail === mtgGuestEmail || mtgGuestEmail.includes(leadEmail)) {
            isSameLead = true;
          }
        }

        // 5. Check guestName (exact or comma-separated/substring match)
        if (!isSameLead && leadName && mtgGuestName) {
          if (leadName === mtgGuestName || mtgGuestName.includes(leadName)) {
            isSameLead = true;
          }
        }

        if (!isSameLead) continue;

        let mStart = new Date(mtg.startTime);
        let mEnd = mtg.endTime
          ? new Date(mtg.endTime)
          : new Date(mStart.getTime() + (Number(mtg.durationMinutes) || 30) * 60000);

        let isOverlapping = slotStart < mEnd && slotEnd > mStart;

        if (
          !isOverlapping &&
          typeof mtg.startTime === 'string' &&
          mtg.startTime.includes(targetDateStr)
        ) {
          const timePart = mtg.startTime.split('T')[1];
          if (timePart) {
            const [mH, mM] = timePart.substring(0, 5).split(':').map(Number);
            const mStartLocal = new Date(y, m - 1, d, mH, mM, 0);
            const mEndLocal = new Date(
              mStartLocal.getTime() + (Number(mtg.durationMinutes) || 30) * 60000
            );
            if (slotStart < mEndLocal && slotEnd > mStartLocal) {
              isOverlapping = true;
              mStart = mStartLocal;
              mEnd = mEndLocal;
            }
          }
        }

        if (isOverlapping) {
          return {
            meetingId: mtg.id,
            title: mtg.title || 'Meeting',
            timeRange: `${formatSlotTimeStr(mStart)} - ${formatSlotTimeStr(mEnd)}`,
            hostName: mtg.hostName || 'Host',
            reason: `Already has meeting (${formatSlotTimeStr(mStart)} - ${formatSlotTimeStr(mEnd)})`,
          };
        }
      }

      return null;
    };
  }, [existingMeetings, date, timeSlot, durationMinutes, isRescheduleMode, initialMeeting]);

  const selectedLeadConflict = useMemo(() => {
    if (!selectedLeadId || leadSourceMode !== 'pipeline') return null;
    const leadObj = (pipelineLeads || []).find((l) => {
      const d = extractLeadDetails(l);
      return d && String(d.id) === String(selectedLeadId);
    });
    if (leadObj) {
      return getLeadConflictForSlot(leadObj);
    }
    return null;
  }, [selectedLeadId, leadSourceMode, pipelineLeads, getLeadConflictForSlot]);

  const isFormValid = useMemo(() => {
    if (isRescheduleLocked) return false;
    if (!isGoogleCalendarConnected) return false;
    if (isSlotInPast(timeSlot, date)) return false;
    if (currentSlotConflict) return false;
    if (selectedLeadConflict) return false;
    if (!meetingTopic.trim()) return false;
    if (isRescheduleMode) return Boolean(date && timeSlot);
    if (leadSourceMode === 'pipeline') {
      return Boolean(guestName.trim() && selectedLeadId);
    }
    return Boolean(
      guestName.trim() && guestEmail.trim() && guestCompany.trim() && guestTitle.trim()
    );
  }, [
    isGoogleCalendarConnected,
    isRescheduleMode,
    leadSourceMode,
    meetingTopic,
    guestName,
    guestEmail,
    selectedLeadId,
    guestCompany,
    guestTitle,
    timeSlot,
    date,
    currentSlotConflict,
    selectedLeadConflict,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isGoogleCalendarConnected) return;
    if (isSlotInPast(timeSlot, date)) return;
    if (!meetingTopic.trim()) return;

    if (!isRescheduleMode) {
      if (leadSourceMode === 'pipeline') {
        if (!guestName.trim() || !selectedLeadId) return;
      } else {
        if (!guestName.trim() || !guestEmail.trim() || !guestCompany.trim() || !guestTitle.trim()) {
          return;
        }
      }
    }

    const [hours, mins] = timeSlot.split(':').map((v) => parseInt(v, 10) || 0);
    const [y, m, d] = date.split('-').map((v) => parseInt(v, 10) || 0);
    const startDate = new Date(y, m - 1, d, hours, mins, 0);
    const startIso = startDate.toISOString();

    const endDate = new Date(startDate.getTime() + (Number(durationMinutes) || 30) * 60000);
    const endIso = endDate.toISOString();

    const generateValidMeetCode = () => {
      const letters = 'abcdefghijklmnopqrstuvwxyz';
      const randStr = (len) =>
        Array.from({ length: len }, () => letters[Math.floor(Math.random() * letters.length)]).join(
          ''
        );
      return `${randStr(3)}-${randStr(4)}-${randStr(3)}`;
    };

    const confUrl =
      conferencingProvider === 'google_meet'
        ? `https://meet.google.com/${generateValidMeetCode()}`
        : conferencingProvider === 'teams'
          ? initialMeeting?.conferencingUrl || ''
          : '';

    const formattedDealEstimate =
      leadSourceMode === 'manual'
        ? formatCurrencyDisplay(dealEstimate, currency) || (CURRENCY_SYMBOLS[currency] || '$') + '0'
        : dealEstimate.trim() || '$0';

    const matchedCreatorHost = (hosts || []).find(
      (h) =>
        (effectiveCurrentUser?.id &&
          (String(h.id) === String(effectiveCurrentUser.id) ||
            String(h.userId) === String(effectiveCurrentUser.id))) ||
        (effectiveCurrentUser?.email &&
          h.email &&
          h.email.toLowerCase() === effectiveCurrentUser.email.toLowerCase()) ||
        (currentUser?.id &&
          (String(h.id) === String(currentUser.id) ||
            String(h.userId) === String(currentUser.id))) ||
        (currentUser?.email && h.email && h.email.toLowerCase() === currentUser.email.toLowerCase())
    );

    const creatorName =
      matchedCreatorHost?.name ||
      effectiveCurrentUser?.name ||
      effectiveCurrentUser?.fullName ||
      effectiveCurrentUser?.displayName ||
      effectiveCurrentUser?.username ||
      (effectiveCurrentUser?.email ? effectiveCurrentUser.email.split('@')[0] : '') ||
      currentUser?.name ||
      currentUser?.fullName ||
      currentUser?.displayName ||
      currentUser?.username ||
      (currentUser?.email ? currentUser.email.split('@')[0] : '') ||
      'Host';

    const creatorEmail =
      effectiveCurrentUser?.email || currentUser?.email || matchedCreatorHost?.email || '';
    const creatorId =
      effectiveCurrentUser?.id ||
      currentUser?.id ||
      currentUser?.sub ||
      matchedCreatorHost?.id ||
      null;
    const originalStartTime = isRescheduleMode
      ? initialMeeting?.originalStartTime ||
        (initialMeeting?.startTime && initialMeeting.startTime !== startIso
          ? initialMeeting.startTime
          : null)
      : null;

    const originalEndTime = isRescheduleMode
      ? initialMeeting?.originalEndTime ||
        (initialMeeting?.startTime && initialMeeting.startTime !== startIso
          ? initialMeeting.endTime
          : null)
      : null;

    const isHostUnassigned = Boolean(
      selectedHost?.isUnassigned || hostId === 'unassigned' || !hostId
    );
    const effectiveCreatorRole = currentUserRole || (isOwner ? 'owner' : 'member');

    const preBrief = initialMeeting?.preMeetingBrief
      ? { ...initialMeeting.preMeetingBrief }
      : {
          accountSummary: `${guestCompany || 'Customer'} opportunity in '${dealStage}' stage with estimated value ${formattedDealEstimate || 'TBD'}.`,
          engagementHistory: `Session scheduled for ${selectedType?.title || 'proposal alignment'}.`,
          primaryObjectives: [
            'Review lead qualification, custom requirements, and proposal ROI.',
            'Align on deal milestones, stakeholders, and procurement timeline.',
          ],
          suggestedTalkTrack: [
            'Walk through customized proposal scope, pricing tiers, and SLA guarantees.',
            'Demonstrate enterprise tenant RLS security and verified outbound deliverability.',
          ],
          keyRisks: 'Evaluate commercial approval steps and budget clearance.',
        };

    if (isHostUnassigned) {
      preBrief.isUnassigned = true;
      preBrief.creatorId = creatorId;
      preBrief.creatorName = creatorName;
      preBrief.creatorEmail = creatorEmail;
      preBrief.creatorRole = effectiveCreatorRole;
    }

    if (selectedLeadId || initialMeeting?.leadId) {
      preBrief.leadId = selectedLeadId || initialMeeting?.leadId || null;
      preBrief.leadName = guestName.trim() || '';
    }

    const finalMeetingTitle = meetingTopic.trim()
      ? `${selectedType?.title || 'Meeting'}: ${meetingTopic.trim()}`
      : `${selectedType?.title || 'Lead Meeting'}: ${guestName.trim() || guestCompany.trim() || 'Lead'}`;

    const newMeeting = {
      ...(initialMeeting || {}),
      id: initialMeeting?.id || `meet-${Date.now()}`,
      title: finalMeetingTitle,
      meetingTypeId: selectedType?.id,
      meetingTypeName: selectedType?.title,
      leadId: selectedLeadId || initialMeeting?.leadId || null,
      isUnassigned: isHostUnassigned,
      hostId: selectedHost.isUnassigned ? creatorId : selectedHost.id,
      hostName: selectedHost.isUnassigned ? creatorName : selectedHost.name,
      hostEmail: selectedHost.isUnassigned ? creatorEmail : selectedHost.email,
      guestName: guestName.trim(),
      guestEmail: guestEmail.trim(),
      guestCompany: guestCompany.trim() || 'Undisclosed Org',
      guestTitle: guestTitle.trim() || 'Lead',
      guestNotes: guestNotes.trim(),
      startTime: startIso,
      endTime: endIso,
      originalStartTime: originalStartTime,
      originalEndTime: originalEndTime,
      durationMinutes: Number(durationMinutes) || 30,
      status: 'confirmed',
      cancellationReason: null,
      conferencingProvider: conferencingProvider,
      conferencingUrl: initialMeeting?.conferencingUrl || confUrl,
      leadScore: Number(leadScore) || 75,
      dealEstimate: formattedDealEstimate,
      currency: currency,
      preMeetingBrief: preBrief,
      postMeetingRecap: initialMeeting?.postMeetingRecap || null,
    };

    onSchedule(newMeeting);
    onClose();
  };

  return (
    <div className="meetings-modal-overlay" onClick={onClose}>
      <div className="sched-clean-modal" onClick={(e) => e.stopPropagation()}>
        {/* Simple Clean Header */}
        <div className="sched-modal-header">
          <div className="sched-modal-header-left">
            <div className="sched-modal-header-icon">
              <i className={isRescheduleMode ? 'fas fa-calendar-alt' : 'fas fa-user-friends'}></i>
            </div>
            <div className="sched-modal-header-info">
              <h2 className="sched-modal-title">
                {isRescheduleMode ? 'Reschedule Meeting' : 'Schedule New Meeting'}
              </h2>
              <p className="sched-modal-subtitle">
                {isRescheduleMode
                  ? 'Choose a new date, time, and session preferences for this meeting.'
                  : 'Set up a session, assign a host, and generate conferencing links.'}
              </p>
            </div>
          </div>
          <button type="button" className="sched-modal-close-btn" onClick={onClose} title="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="sched-modal-form">
          <div className="sched-modal-body">
            {isRescheduleLocked && (
              <div
                className="sched-google-cal-warning-banner"
                style={{ borderColor: '#fde68a', background: '#fef3c7' }}
              >
                <div
                  className="sched-warning-banner-icon"
                  style={{ background: '#fef3c7', color: '#d97706' }}
                >
                  <i className="fas fa-lock"></i>
                </div>
                <div className="sched-warning-banner-content">
                  <div className="sched-warning-banner-title" style={{ color: '#92400e' }}>
                    Rescheduling Disabled
                  </div>
                  <div className="sched-warning-banner-text" style={{ color: '#b45309' }}>
                    This meeting is already open to join (within 15 minutes of start time) or
                    currently in progress. Rescheduling is disabled.
                  </div>
                </div>
              </div>
            )}

            {!isCalendarConnected && (
              <div className="sched-google-cal-warning-banner">
                <div className="sched-warning-banner-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <div className="sched-warning-banner-content">
                  <div className="sched-warning-banner-title">Calendar Not Connected</div>
                  <div className="sched-warning-banner-text">
                    You must connect your Google or Microsoft Calendar in{' '}
                    <strong>Availability & Rules</strong> before scheduling meetings to generate
                    valid video meeting links and sync calendar availability.
                  </div>
                </div>
              </div>
            )}

            {/* Section 1: Session Details */}
            <div className="sched-section-block">
              <div className="sched-section-header">
                <span className="sched-section-title">Session Details</span>
              </div>

              <div className="sched-grid-2">
                {/* 1. Host Dropdown */}
                <div className="sched-form-group" ref={hostRef}>
                  <label className="sched-label">Assigned Host</label>
                  <div className="sched-dropdown-wrap">
                    <button
                      type="button"
                      className={`sched-select-trigger ${isHostOpen ? 'active' : ''}`}
                      onClick={() => {
                        setIsHostOpen(!isHostOpen);
                        setIsTypeOpen(false);
                        setIsTimeOpen(false);
                      }}
                    >
                      <div className="sched-trigger-left">
                        {selectedHost?.isUnassigned ? (
                          <>
                            <span className="sched-unassigned-avatar">
                              <i className="fas fa-user-slash"></i>
                            </span>
                            <span className="sched-host-selected-name" style={{ color: '#64748b' }}>
                              Unassigned
                            </span>
                          </>
                        ) : (
                          <>
                            {selectedHost?.avatarUrl ? (
                              <img
                                src={selectedHost.avatarUrl}
                                alt={selectedHost.name}
                                className="sched-host-avatar-img"
                              />
                            ) : (
                              <span
                                className="sched-host-avatar-pill"
                                style={{
                                  backgroundColor: getAvatarColor(
                                    selectedHost?.name || selectedHost?.username
                                  ),
                                }}
                              >
                                {selectedHost?.avatar || getInitials(selectedHost?.name)}
                              </span>
                            )}
                            <span className="sched-host-selected-name">{selectedHost?.name}</span>
                            {selectedHost?.role && (
                              <span className="sched-host-role-badge">
                                {formatRoleLabel(selectedHost.role)}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <i
                        className={`fas fa-chevron-down sched-chevron ${isHostOpen ? 'open' : ''}`}
                      ></i>
                    </button>

                    {isHostOpen && (
                      <div className="sched-dropdown-menu">
                        <div className="sched-dropdown-options-list">
                          {/* Unassigned Option (Always first) */}
                          <div
                            className={`sched-dropdown-option-item ${
                              !hostId || hostId === 'unassigned' ? 'selected' : ''
                            }`}
                            onClick={() => {
                              setHostId('');
                              setIsHostOpen(false);
                            }}
                          >
                            <div className="sched-opt-host-left">
                              <span className="sched-unassigned-avatar">
                                <i className="fas fa-user-slash"></i>
                              </span>
                              <div className="sched-opt-host-details">
                                <div className="sched-opt-host-name-row">
                                  <span className="sched-opt-name" style={{ color: '#475569' }}>
                                    Unassigned
                                  </span>
                                </div>
                                <span className="sched-opt-email">No host assigned currently</span>
                              </div>
                            </div>
                            {(!hostId || hostId === 'unassigned') && (
                              <i className="fas fa-check sched-opt-check"></i>
                            )}
                          </div>

                          {/* Member Host Options (Lower hierarchy levels only) */}
                          {assignableHosts && assignableHosts.length > 0
                            ? assignableHosts.map((h) => {
                                const isSelected = h.id === hostId;
                                return (
                                  <div
                                    key={h.id}
                                    className={`sched-dropdown-option-item ${
                                      isSelected ? 'selected' : ''
                                    }`}
                                    onClick={() => {
                                      setHostId(h.id);
                                      setIsHostOpen(false);
                                    }}
                                  >
                                    <div className="sched-opt-host-left">
                                      {h.avatarUrl ? (
                                        <img
                                          src={h.avatarUrl}
                                          alt={h.name}
                                          className="sched-host-avatar-img"
                                        />
                                      ) : (
                                        <span
                                          className="sched-host-avatar-pill"
                                          style={{
                                            backgroundColor: getAvatarColor(h.name || h.username),
                                          }}
                                        >
                                          {h.avatar || getInitials(h.name)}
                                        </span>
                                      )}
                                      <div className="sched-opt-host-details">
                                        <div className="sched-opt-host-name-row">
                                          <span className="sched-opt-name">{h.name}</span>
                                          <span className="sched-opt-role-tag">
                                            {formatRoleLabel(h.role)}
                                          </span>
                                        </div>
                                        {h.email && (
                                          <span className="sched-opt-email">{h.email}</span>
                                        )}
                                      </div>
                                    </div>
                                    {isSelected && <i className="fas fa-check sched-opt-check"></i>}
                                  </div>
                                );
                              })
                            : null}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 2. Meeting Type Dropdown */}
                <div className="sched-form-group" ref={typeRef}>
                  <label className="sched-label">
                    Meeting Type <span className="sched-req">*</span>
                  </label>
                  <div className="sched-dropdown-wrap">
                    <button
                      type="button"
                      className={`sched-select-trigger ${isTypeOpen ? 'active' : ''}`}
                      onClick={() => {
                        setIsTypeOpen(!isTypeOpen);
                        setIsHostOpen(false);
                        setIsTimeOpen(false);
                      }}
                    >
                      <div className="sched-trigger-left">
                        <span className="sched-type-selected-title">{selectedType?.title}</span>
                      </div>
                      <i
                        className={`fas fa-chevron-down sched-chevron ${isTypeOpen ? 'open' : ''}`}
                      ></i>
                    </button>

                    {isTypeOpen && (
                      <div className="sched-dropdown-menu">
                        <div className="sched-dropdown-options-list">
                          {meetingTypes.map((t) => {
                            const isSelected = t.id === meetingTypeId;
                            return (
                              <div
                                key={t.id}
                                className={`sched-dropdown-option-item ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  setMeetingTypeId(t.id);
                                  setIsTypeOpen(false);
                                }}
                              >
                                <div className="sched-opt-type-left">
                                  <div className="sched-opt-type-details">
                                    <span className="sched-opt-type-title">{t.title}</span>
                                    {t.description && (
                                      <p className="sched-opt-type-desc">{t.description}</p>
                                    )}
                                  </div>
                                </div>
                                {isSelected && <i className="fas fa-check sched-opt-check"></i>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Meeting Title / Topic */}
              <div className="sched-form-group">
                <label className="sched-label">
                  Meeting Title / Topic <span className="sched-req">*</span>
                </label>
                <div className="sched-title-input-wrap">
                  <span className="sched-title-prefix">{selectedType?.title || 'Meeting'}:</span>
                  <input
                    type="text"
                    className="sched-title-input-field"
                    placeholder="Enter meeting topic (e.g. Solution Walkthrough, Q4 Strategy)"
                    value={meetingTopic}
                    onChange={(e) => {
                      setHasUserEditedTopic(true);
                      setMeetingTopic(e.target.value);
                    }}
                    required
                  />
                </div>
              </div>

              {/* 3. Clean Duration Pills */}
              <div className="sched-form-group">
                <div className="sched-label-row">
                  <label className="sched-label">
                    Duration <span className="sched-req">*</span>
                  </label>
                  <span className="sched-duration-selected-tag">{durationMinutes} min</span>
                </div>
                <div className="sched-duration-pills">
                  {DURATION_OPTIONS.map((dur) => (
                    <button
                      key={dur}
                      type="button"
                      className={`sched-dur-btn ${durationMinutes === dur ? 'active' : ''}`}
                      onClick={() => setDurationMinutes(dur)}
                    >
                      {dur} min
                    </button>
                  ))}
                </div>
              </div>

              {/* 4. Date and Time Slot */}
              <div className="sched-grid-2">
                <div className="sched-form-group">
                  <label className="sched-label">
                    Date <span className="sched-req">*</span>
                  </label>
                  <input
                    type="date"
                    min={todayStr}
                    value={date}
                    onChange={(e) => {
                      setDate(e.target.value);
                    }}
                    className="sched-input"
                    required
                  />
                </div>

                <div className="sched-form-group" ref={timeRef}>
                  <label className="sched-label">
                    Time Slot <span className="sched-req">*</span>
                  </label>
                  <div className="sched-dropdown-wrap">
                    <button
                      type="button"
                      className={`sched-select-trigger ${isTimeOpen ? 'active' : ''} ${currentSlotConflict ? 'has-conflict' : ''}`}
                      onClick={() => {
                        setIsTimeOpen(!isTimeOpen);
                        setIsHostOpen(false);
                        setIsTypeOpen(false);
                      }}
                    >
                      <div className="sched-trigger-left">
                        <i
                          className={`far fa-clock ${currentSlotConflict ? 'text-danger' : 'text-muted'}`}
                        ></i>
                        <span>{timeSlot}</span>
                        {currentSlotConflict && (
                          <span
                            className="sched-conflict-source-tag"
                            style={{ marginLeft: '6px', fontSize: '10px' }}
                          >
                            {currentSlotConflict.isBuffer ? 'Buffer' : 'Conflict'}
                          </span>
                        )}
                      </div>
                      <i
                        className={`fas fa-chevron-down sched-chevron ${isTimeOpen ? 'open' : ''}`}
                      ></i>
                    </button>

                    {isTimeOpen && (
                      <div className="sched-dropdown-menu time-menu">
                        <div className="sched-time-menu-header">
                          <span className="sched-time-header-hours">
                            <i className="far fa-clock"></i> Host Hours:{' '}
                            {formatTimeDisplay(hostWorkingHoursStart)} -{' '}
                            {formatTimeDisplay(hostWorkingHoursEnd)}
                          </span>
                          {(bufferBeforeMinutes > 0 || bufferAfterMinutes > 0) && (
                            <span
                              className="sched-time-header-buffer"
                              style={{
                                fontSize: '11px',
                                color: '#64748b',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              <i
                                className="fas fa-hourglass-half"
                                style={{ fontSize: '10px', color: '#f59e0b' }}
                              ></i>
                              Buffers:{' '}
                              {bufferBeforeMinutes > 0 ? `${bufferBeforeMinutes}m before` : ''}
                              {bufferBeforeMinutes > 0 && bufferAfterMinutes > 0 ? ' • ' : ''}
                              {bufferAfterMinutes > 0 ? `${bufferAfterMinutes}m after` : ''}
                            </span>
                          )}
                        </div>
                        <div className="sched-time-slots-grid">
                          {dynamicTimeSlots.map((slot) => {
                            const isSelected = slot === timeSlot;
                            const isPast = isSlotInPast(slot, date);
                            const slotConflict = slotsConflictMap[slot];
                            const isConflicted = !isPast && Boolean(slotConflict);

                            let chipTitle = `Select ${slot}`;
                            if (isPast) {
                              chipTitle = 'This time slot has already passed';
                            } else if (isConflicted) {
                              if (slotConflict.isBuffer) {
                                chipTitle = `Buffer Conflict: ${slotConflict.bufferNotice || `Requires ${slotConflict.bufferMinutes}m buffer time.`} (${slotConflict.title})`;
                              } else {
                                chipTitle = `Conflict: ${slotConflict.sourceLabel} - ${slotConflict.title} (${slotConflict.timeRange})`;
                              }
                            }

                            return (
                              <button
                                key={slot}
                                type="button"
                                disabled={isPast}
                                className={`sched-time-chip ${isSelected ? 'active' : ''} ${isPast ? 'disabled' : ''} ${isConflicted ? 'conflicted' : ''}`}
                                title={chipTitle}
                                onClick={() => {
                                  if (isPast) return;
                                  setTimeSlot(slot);
                                  setIsTimeOpen(false);
                                }}
                              >
                                {slot}
                              </button>
                            );
                          })}
                        </div>
                        {dynamicTimeSlots.every((s) => isSlotInPast(s, date)) && (
                          <div
                            className="sched-no-slots-hint"
                            style={{
                              padding: '8px 10px',
                              fontSize: '11.5px',
                              color: '#ef4444',
                              textAlign: 'center',
                            }}
                          >
                            All time slots for today have passed. Please select a future date.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Real-time Conflict Warning & Availability Indicator */}
              {isCheckingConflicts ? (
                <div className="sched-conflict-status-bar checking">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>
                    Checking live calendar conflicts for{' '}
                    {effectiveTargetPerson.isSelf ? 'current account' : effectiveTargetPerson.name}
                    ...
                  </span>
                </div>
              ) : currentSlotConflict ? (
                <div
                  className={`sched-conflict-alert-box ${currentSlotConflict.isBuffer ? 'buffer-conflict' : ''}`}
                >
                  <div className="sched-conflict-alert-main">
                    <div className="sched-conflict-alert-icon">
                      <i
                        className={
                          currentSlotConflict.isBuffer
                            ? 'fas fa-hourglass-half'
                            : 'fas fa-calendar-times'
                        }
                      ></i>
                    </div>
                    <div className="sched-conflict-alert-text">
                      <div className="sched-conflict-alert-title-row">
                        <span className="sched-conflict-alert-title">
                          {currentSlotConflict.isBuffer
                            ? 'Scheduling Buffer Conflict'
                            : 'Schedule Conflict Detected'}
                        </span>
                        <span className="sched-conflict-source-tag">
                          {currentSlotConflict.isBuffer
                            ? `${currentSlotConflict.bufferMinutes}m Buffer`
                            : currentSlotConflict.sourceLabel}{' '}
                          •{' '}
                          {effectiveTargetPerson.isSelf
                            ? 'Current Account'
                            : effectiveTargetPerson.name}
                        </span>
                      </div>
                      <p className="sched-conflict-alert-msg">
                        {currentSlotConflict.isBuffer ? (
                          <>
                            {currentSlotConflict.bufferNotice ||
                              `Requires ${currentSlotConflict.bufferMinutes}m buffer before/after meeting.`}{' '}
                            <span className="sched-conflict-time-muted">
                              (Adjacent to "{currentSlotConflict.title}" at{' '}
                              {currentSlotConflict.timeRange})
                            </span>
                          </>
                        ) : (
                          <>
                            Conflicts with <strong>"{currentSlotConflict.title}"</strong>{' '}
                            <span className="sched-conflict-time-muted">
                              ({currentSlotConflict.timeRange || computedTimeRange})
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {smartMultiDaySuggestions.length > 0 && (
                    <div className="sched-conflict-suggestions-pane">
                      <div className="sched-conflict-sugg-header">
                        <span className="sched-conflict-sugg-title">
                          Recommended Available Slots ({durationMinutes}m)
                        </span>
                        <span className="sched-conflict-sugg-hint">Click a slot to switch</span>
                      </div>
                      <div className="sched-conflict-sugg-days-list">
                        {Object.entries(
                          smartMultiDaySuggestions.reduce((acc, item) => {
                            acc[item.dateLabel] = acc[item.dateLabel] || [];
                            acc[item.dateLabel].push(item);
                            return acc;
                          }, {})
                        ).map(([dateLabel, slots]) => (
                          <div key={dateLabel} className="sched-sugg-day-group">
                            <span className="sched-sugg-day-label">{dateLabel}</span>
                            <div className="sched-sugg-day-chips">
                              {slots.map((item) => (
                                <button
                                  key={`${item.dateStr}-${item.slot}`}
                                  type="button"
                                  className="sched-sugg-slot-chip"
                                  onClick={() => {
                                    setDate(item.dateStr);
                                    setTimeSlot(item.slot);
                                  }}
                                  title={`Switch to ${item.dateLabel} at ${item.timeLabel}`}
                                >
                                  {item.timeLabel}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : date && timeSlot && !isSlotInPast(timeSlot, date) ? (
                !isWorkingDay(date) ? (
                  <div className="sched-conflict-status-bar non-working">
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>
                      <strong>Non-Working Day</strong> ({computedTimeRange}) — Selected date is
                      outside active working days (
                      {hostWorkingDays.map((d) => d.slice(0, 3)).join(', ')})
                    </span>
                  </div>
                ) : (
                  <div className="sched-conflict-status-bar available">
                    <i className="fas fa-check-circle"></i>
                    <span>
                      Available slot ({computedTimeRange}) — No conflicts detected for{' '}
                      {effectiveTargetPerson.isSelf
                        ? 'current account'
                        : effectiveTargetPerson.name}
                    </span>
                  </div>
                )
              ) : null}

              {/* 5. Clean Video Conferencing Selector */}
              <div className="sched-form-group">
                <label className="sched-label">Video Conferencing</label>
                <div className="sched-platform-segmented">
                  <button
                    type="button"
                    className={`sched-plat-btn ${conferencingProvider === 'google_meet' ? 'active' : ''} ${
                      connectedCalendarProvider && connectedCalendarProvider !== 'google'
                        ? 'sched-plat-btn-disabled'
                        : ''
                    }`}
                    onClick={() => {
                      if (connectedCalendarProvider === 'microsoft') {
                        setPlatformNotice(
                          'Google Meet requires a connected Google Calendar. Currently connected to Microsoft.'
                        );
                        return;
                      }
                      setConferencingProvider('google_meet');
                      setPlatformNotice(null);
                    }}
                    title={
                      connectedCalendarProvider === 'microsoft'
                        ? 'Google Meet requires Google Calendar connection'
                        : 'Google Meet'
                    }
                  >
                    <i className="fab fa-google text-google" />
                    <span>Google Meet</span>
                  </button>

                  <button
                    type="button"
                    className={`sched-plat-btn ${conferencingProvider === 'teams' ? 'active' : ''} ${
                      connectedCalendarProvider !== 'microsoft' ? 'sched-plat-btn-disabled' : ''
                    }`}
                    onClick={() => {
                      if (connectedCalendarProvider !== 'microsoft') {
                        setPlatformNotice(
                          'MS Teams integration requires a connected Microsoft Calendar. Please connect in Availability & Rules.'
                        );
                        return;
                      }
                      setConferencingProvider('teams');
                      setPlatformNotice(null);
                    }}
                    title={
                      connectedCalendarProvider === 'microsoft'
                        ? 'Microsoft Teams'
                        : 'Connect Microsoft Calendar to use MS Teams'
                    }
                  >
                    <i className="fab fa-microsoft text-msteams" />
                    <span>MS Teams</span>
                  </button>
                </div>
                {platformNotice && (
                  <div className="sched-platform-dev-notice">
                    <i className="fas fa-info-circle"></i>
                    <span>{platformNotice}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Section 2: Lead & Deal Information */}
            <div className="sched-section-block">
              <div className="sched-section-header">
                <span className="sched-section-title">
                  {isRescheduleMode ? 'Participant & Lead Context' : 'Lead & Deal Information'}
                </span>
                {!isRescheduleMode && (
                  <div className="sched-source-segmented">
                    <button
                      type="button"
                      className={`sched-source-btn ${leadSourceMode === 'pipeline' ? 'active' : ''}`}
                      onClick={handleSwitchToPipeline}
                    >
                      <i className="fas fa-database" />
                      <span>Import from Pipeline</span>
                    </button>
                    <button
                      type="button"
                      className={`sched-source-btn ${leadSourceMode === 'manual' ? 'active' : ''}`}
                      onClick={handleSwitchToManual}
                    >
                      <i className="fas fa-user-edit" />
                      <span>Manual Fill</span>
                    </button>
                  </div>
                )}
              </div>

              {/* In Reschedule Mode: Display clean read-only lead & account summary card or Compact Group Cohort Chips */}
              {isRescheduleMode ? (
                isRescheduleGroupSession && rescheduleAttendeesList.length > 1 ? (
                  <div className="sched-group-chips-card">
                    <div className="sched-group-chips-header">
                      <div className="sched-group-chips-header-left">
                        <span className="sched-group-chips-icon">
                          <i className="fas fa-bullhorn" />
                        </span>
                        <div className="sched-group-chips-title-wrap">
                          <span className="sched-group-chips-campaign-label">
                            Campaign Cohort Session
                          </span>
                          <strong className="sched-group-chips-campaign-name">
                            {initialMeeting?.campaignName || 'Target Audience Cohort'}
                          </strong>
                        </div>
                      </div>
                      <span className="sched-group-chips-count-badge">
                        <i className="fas fa-users" />
                        <span>{rescheduleAttendeesList.length} Attendees</span>
                      </span>
                    </div>

                    <div className="sched-group-chips-body">
                      <div className="sched-group-chips-grid">
                        {rescheduleAttendeesList.map((att, idx) => (
                          <div
                            key={att.id || idx}
                            className="sched-attendee-chip"
                            title={`${att.name}${att.jobTitle ? ` · ${att.jobTitle}` : ''}${att.company ? ` (${att.company})` : ''}${att.email ? `\n${att.email}` : ''}`}
                          >
                            <span
                              className="sched-chip-avatar"
                              style={{ backgroundColor: getAvatarColor(att.name) }}
                            >
                              {getInitials(att.name)}
                            </span>
                            <div className="sched-chip-info">
                              <span className="sched-chip-name">{att.name}</span>
                              {att.company && (
                                <span className="sched-chip-company">@{att.company}</span>
                              )}
                            </div>
                            {att.stage && (
                              <span
                                className={`sched-chip-stage stage-${String(
                                  att.stage || 'contacted'
                                )
                                  .toLowerCase()
                                  .replace(/\s+/g, '-')}`}
                              >
                                {att.stage}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="sched-selected-lead-card">
                    <div className="sched-lead-card-header">
                      <div className="sched-selected-lead-card-left">
                        <span
                          className="sched-lead-avatar sched-lead-avatar-lg"
                          style={{ backgroundColor: getAvatarColor(guestName) }}
                        >
                          {getInitials(guestName)}
                        </span>
                        <div className="sched-selected-lead-details">
                          <div className="sched-selected-lead-top">
                            <strong className="sched-selected-lead-name">
                              {guestName || 'Invited Guest'}
                            </strong>
                            {guestTitle && (
                              <span className="sched-selected-lead-title">{guestTitle}</span>
                            )}
                            {dealStage && (
                              <span
                                className={`sched-lead-stage-badge stage-${String(
                                  dealStage || 'new'
                                )
                                  .toLowerCase()
                                  .replace(/\s+/g, '-')}`}
                              >
                                {dealStage}
                              </span>
                            )}
                          </div>
                          <div className="sched-selected-lead-bottom">
                            {guestCompany && (
                              <span className="sched-lead-meta-item">
                                <i className="fas fa-building" />
                                {guestCompany}
                              </span>
                            )}
                            {guestEmail && (
                              <span className="sched-lead-meta-item">
                                <i className="fas fa-envelope" />
                                {guestEmail}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="sched-lead-card-details-grid">
                      <div className="sched-lead-card-detail-item">
                        <span className="sched-card-detail-label">Work Email</span>
                        <span className="sched-card-detail-value">{guestEmail || '—'}</span>
                      </div>
                      <div className="sched-lead-card-detail-item">
                        <span className="sched-card-detail-label">Company / Account</span>
                        <span className="sched-card-detail-value">{guestCompany || '—'}</span>
                      </div>
                      <div className="sched-lead-card-detail-item">
                        <span className="sched-card-detail-label">Pipeline Stage</span>
                        <span className="sched-card-detail-value">
                          <span
                            className={`sched-lead-stage-badge stage-${String(dealStage || 'new')
                              .toLowerCase()
                              .replace(/\s+/g, '-')}`}
                          >
                            {dealStage || 'New'}
                          </span>
                        </span>
                      </div>
                      <div className="sched-lead-card-detail-item">
                        <span className="sched-card-detail-label">Estimated Deal Value</span>
                        <span className="sched-card-detail-value sched-card-deal-value">
                          {dealEstimate || '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <>
                  {/* 1. Pipeline Mode: Lead Selector & Rich Selected Lead Card */}
                  {leadSourceMode === 'pipeline' && (
                    <div className="sched-pipeline-lead-selector" ref={leadDropdownRef}>
                      {selectedLeadId ? (
                        <div className="sched-selected-lead-card">
                          <div className="sched-lead-card-header">
                            <div className="sched-selected-lead-card-left">
                              <span
                                className="sched-lead-avatar sched-lead-avatar-lg"
                                style={{ backgroundColor: getAvatarColor(guestName) }}
                              >
                                {getInitials(guestName)}
                              </span>
                              <div className="sched-selected-lead-details">
                                <div className="sched-selected-lead-top">
                                  <strong className="sched-selected-lead-name">{guestName}</strong>
                                  {guestTitle && (
                                    <span className="sched-selected-lead-title">{guestTitle}</span>
                                  )}
                                  {dealStage && (
                                    <span
                                      className={`sched-lead-stage-badge stage-${String(
                                        dealStage || 'new'
                                      )
                                        .toLowerCase()
                                        .replace(/\s+/g, '-')}`}
                                    >
                                      {dealStage}
                                    </span>
                                  )}
                                </div>
                                <div className="sched-selected-lead-bottom">
                                  {guestCompany && (
                                    <span className="sched-lead-meta-item">
                                      <i className="fas fa-building" />
                                      {guestCompany}
                                    </span>
                                  )}
                                  {guestEmail && (
                                    <span className="sched-lead-meta-item">
                                      <i className="fas fa-envelope" />
                                      {guestEmail}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="sched-change-lead-btn"
                              onClick={handleChangeLead}
                              title="Choose another lead from CRM pipeline"
                            >
                              <i className="fas fa-sync-alt" style={{ marginRight: '5px' }} />
                              Change Lead
                            </button>
                          </div>

                          <div className="sched-lead-card-details-grid">
                            <div className="sched-lead-card-detail-item">
                              <span className="sched-card-detail-label">Work Email</span>
                              <span className="sched-card-detail-value">{guestEmail || '—'}</span>
                            </div>
                            <div className="sched-lead-card-detail-item">
                              <span className="sched-card-detail-label">Company / Account</span>
                              <span className="sched-card-detail-value">{guestCompany || '—'}</span>
                            </div>
                            <div className="sched-lead-card-detail-item">
                              <span className="sched-card-detail-label">Pipeline Stage</span>
                              <span className="sched-card-detail-value">
                                <span
                                  className={`sched-lead-stage-badge stage-${String(
                                    dealStage || 'new'
                                  )
                                    .toLowerCase()
                                    .replace(/\s+/g, '-')}`}
                                >
                                  {dealStage || 'New'}
                                </span>
                              </span>
                            </div>
                            <div className="sched-lead-card-detail-item">
                              <span className="sched-card-detail-label">Estimated Deal Value</span>
                              <span className="sched-card-detail-value sched-card-deal-value">
                                {dealEstimate || '—'}
                              </span>
                            </div>
                          </div>

                          {selectedLeadConflict && (
                            <div className="sched-lead-selected-conflict-warning">
                              <i className="fas fa-exclamation-triangle"></i>
                              <span>
                                <strong>Lead Unavailable:</strong> This lead already has another
                                meeting booked in this time slot ({selectedLeadConflict.timeRange}).
                                Please select a different time or change lead.
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="sched-lead-search-input-wrap">
                            <i className="fas fa-search sched-lead-search-icon" />
                            <input
                              type="text"
                              placeholder="Search lead in CRM pipeline by name, company, email, or stage..."
                              value={leadSearchQuery}
                              onChange={(e) => {
                                setLeadSearchQuery(e.target.value);
                                setIsLeadDropdownOpen(true);
                              }}
                              onFocus={() => setIsLeadDropdownOpen(true)}
                              className="sched-lead-search-input"
                            />

                            {isLeadDropdownOpen && (
                              <div className="sched-lead-picker-dropdown">
                                {isLoadingLeads ? (
                                  <div className="sched-dropdown-empty">
                                    <i
                                      className="fas fa-spinner fa-spin"
                                      style={{ marginRight: '6px' }}
                                    />
                                    Loading pipeline leads...
                                  </div>
                                ) : filteredPipelineLeads.length > 0 ? (
                                  filteredPipelineLeads.map((rawLead) => {
                                    const lead = extractLeadDetails(rawLead);
                                    if (!lead) return null;
                                    const leadConflict = getLeadConflictForSlot(rawLead);
                                    const isConflicted = Boolean(leadConflict);

                                    return (
                                      <div
                                        key={lead.id}
                                        className={`sched-lead-picker-item ${isConflicted ? 'conflicted' : ''}`}
                                        onClick={() => {
                                          if (isConflicted) return; // Prevent selecting conflicted lead
                                          handleSelectPipelineLead(rawLead);
                                        }}
                                        title={
                                          isConflicted
                                            ? `Cannot select: ${lead.name || 'Lead'} already has a meeting scheduled during this time slot (${leadConflict.timeRange})`
                                            : `Import ${lead.name || 'lead'}`
                                        }
                                      >
                                        <div className="sched-lead-picker-left">
                                          <span
                                            className="sched-lead-avatar"
                                            style={{
                                              backgroundColor: isConflicted
                                                ? '#94a3b8'
                                                : getAvatarColor(lead.name),
                                            }}
                                          >
                                            {getInitials(lead.name)}
                                          </span>
                                          <div className="sched-lead-picker-info">
                                            <div className="sched-lead-picker-name-row">
                                              <span className="sched-lead-picker-name">
                                                {lead.name || 'Unnamed Lead'}
                                              </span>
                                              {lead.company && (
                                                <span className="sched-lead-picker-company">
                                                  · {lead.company}
                                                </span>
                                              )}
                                            </div>
                                            <span className="sched-lead-picker-sub">
                                              {lead.jobTitle ? `${lead.jobTitle} ` : ''}
                                              {lead.email ? `(${lead.email})` : ''}
                                            </span>
                                            {isConflicted && (
                                              <span className="sched-lead-conflict-hint">
                                                <i className="fas fa-ban"></i> {leadConflict.reason}
                                              </span>
                                            )}
                                          </div>
                                        </div>

                                        <div className="sched-lead-picker-right">
                                          {isConflicted ? (
                                            <span
                                              className="sched-lead-conflict-badge"
                                              title={leadConflict.reason}
                                            >
                                              <i className="fas fa-calendar-times"></i> Busy
                                            </span>
                                          ) : (
                                            <span
                                              className={`sched-lead-stage-badge stage-${String(
                                                lead.stage || 'new'
                                              )
                                                .toLowerCase()
                                                .replace(/\s+/g, '-')}`}
                                            >
                                              {lead.stage}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="sched-dropdown-empty">
                                    {leadSearchQuery
                                      ? `No pipeline leads matching "${leadSearchQuery}"`
                                      : 'No leads found in pipeline'}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="sched-pipeline-empty-hint">
                            <div className="sched-empty-icon-wrap">
                              <i className="fas fa-address-book" />
                            </div>
                            <div className="sched-empty-hint-text">
                              <h5>Select a Lead from CRM Pipeline</h5>
                              <p>
                                Type above to search by lead name, company, email, or stage to link
                                verified CRM data.
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* 2. Manual Fill Mode: Form Input Fields */}
                  {leadSourceMode === 'manual' && (
                    <>
                      <div className="sched-grid-2">
                        <div className="sched-form-group">
                          <label className="sched-label">
                            Guest Name <span className="sched-req">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Jennifer Wong"
                            value={guestName}
                            onChange={(e) => {
                              const val = e.target.value;
                              setGuestName(val);
                              if (!hasUserEditedTopic) {
                                setMeetingTopic(val);
                              }
                            }}
                            className="sched-input"
                            required
                          />
                        </div>

                        <div className="sched-form-group">
                          <label className="sched-label">
                            Work Email <span className="sched-req">*</span>
                          </label>
                          <input
                            type="email"
                            placeholder="e.g. jennifer@acme.com"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            className="sched-input"
                            required
                          />
                        </div>
                      </div>

                      <div className="sched-grid-2">
                        <div className="sched-form-group">
                          <label className="sched-label">
                            Company / Account <span className="sched-req">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Acme Corporation"
                            value={guestCompany}
                            onChange={(e) => setGuestCompany(e.target.value)}
                            className="sched-input"
                            required
                          />
                        </div>

                        <div className="sched-form-group">
                          <label className="sched-label">
                            Job Title <span className="sched-req">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. VP of Business Dev"
                            value={guestTitle}
                            onChange={(e) => setGuestTitle(e.target.value)}
                            className="sched-input"
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="sched-form-group">
                <label className="sched-label">Agenda / Meeting Notes</label>
                <textarea
                  rows={2}
                  placeholder="Key discussion topics, proposal objectives, or qualification details..."
                  value={guestNotes}
                  onChange={(e) => setGuestNotes(e.target.value)}
                  className="sched-textarea"
                />
              </div>
            </div>
          </div>

          {/* Simple Clean Footer */}
          <div className="sched-modal-footer">
            <div className="sched-footer-summary">
              <i className="far fa-calendar-check sched-summary-icon" />
              <span>
                <strong>{formattedDisplayDate || date}</strong> · {computedTimeRange} (
                {durationMinutes}m) ·{' '}
                {conferencingProvider === 'google_meet'
                  ? 'Google Meet'
                  : conferencingProvider === 'teams'
                    ? 'MS Teams'
                    : 'Zoom'}
              </span>
            </div>

            <div className="sched-footer-actions">
              <button type="button" className="sched-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className={`sched-btn-primary ${currentSlotConflict ? 'has-conflict-lock' : ''} ${!isGoogleCalendarConnected || isRescheduleLocked ? 'has-cal-lock' : ''}`}
                disabled={!isFormValid || isRescheduleLocked}
                title={
                  isRescheduleLocked
                    ? 'Rescheduling is locked because this meeting is already open to join or in progress.'
                    : !isGoogleCalendarConnected
                      ? 'Please connect your Google or Microsoft Calendar in Availability & Rules before scheduling.'
                      : currentSlotConflict
                        ? currentSlotConflict.isBuffer
                          ? `Buffer conflict: Host requires ${currentSlotConflict.bufferMinutes}m buffer time.`
                          : 'Schedule conflict detected. Please select an available slot above.'
                        : ''
                }
              >
                {isRescheduleLocked
                  ? 'Rescheduling Disabled'
                  : !isGoogleCalendarConnected
                    ? 'Connect Calendar to Schedule'
                    : currentSlotConflict
                      ? currentSlotConflict.isBuffer
                        ? 'Buffer Conflict'
                        : 'Conflict Detected'
                      : isRescheduleMode
                        ? 'Save & Reschedule'
                        : 'Schedule Meeting'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
